import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import ReactMarkdown from "react-markdown";
import RichTextEditor from "../components/RichTextEditor";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { 
  Users, Building2, Settings, Shield, Activity, 
  Eye, EyeOff, Key, LogIn, RefreshCw, Search,
  ChevronDown, ChevronRight, BarChart3, Clock,
  GripVertical,
  Globe, Database, Zap, AlertTriangle, CheckCircle,
  XCircle, Copy, Trash2, Edit, Plus, Lock, Unlock,
  TrendingUp, MousePointer, Timer, Calendar,
  LayoutDashboard, FileText, PieChart, LineChart,
  BookOpen, ExternalLink, Brain, Sparkles, FileBarChart,
  Download, Send, Image as ImageIcon, FileArchive,
  ToggleLeft, Flag, Beaker
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Master password configuration
const MASTER_PASSWORD_LENGTH = 20;
const MASTER_PASSWORD_VALIDITY_HOURS = 4;

// Sortable Widget Item Component for drag & drop
function SortableWidgetItem({ widget, pageId, widgetEnabled, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 ${isDragging ? 'bg-purple-50 shadow-md' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 touch-none"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <label className="flex items-center gap-2 cursor-pointer flex-1">
        <input
          type="checkbox"
          checked={widgetEnabled}
          onChange={(e) => onToggle(pageId, widget.id, e.target.checked)}
          className="rounded border-slate-300 text-purple-600"
        />
        <span className="text-xs text-slate-600 flex-1">{widget.name}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          widget.type === "metric" ? "bg-blue-100 text-blue-700" :
          widget.type === "chart" ? "bg-green-100 text-green-700" :
          widget.type === "table" ? "bg-amber-100 text-amber-700" :
          "bg-slate-100 text-slate-600"
        }`}>
          {widget.type}
        </span>
      </label>
    </div>
  );
}

export default function SuperAdminPanelPage() {
  const { sessionToken, user } = useAuth();
  
  // Tenant Management State
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTenants, setExpandedTenants] = useState({});
  
  // Master Password State
  const [masterPassword, setMasterPassword] = useState(null);
  const [masterPasswordExpiry, setMasterPasswordExpiry] = useState(null);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  
  // User Management Dialogs
  const [setPasswordDialog, setSetPasswordDialog] = useState({ open: false, user: null, tenantId: null });
  const [newPassword, setNewPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  
  // Analytics State
  const [analyticsData, setAnalyticsData] = useState({
    totalUsers: 0,
    activeToday: 0,
    totalSessions: 0,
    pageViews: {},
    userActivity: []
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Sync Status State
  const [syncStatus, setSyncStatus] = useState({});
  const [syncing, setSyncing] = useState({});

  // Sync Logs Cleanup State
  const [syncLogsStats, setSyncLogsStats] = useState(null);
  const [loadingSyncLogs, setLoadingSyncLogs] = useState(false);
  const [cleanupDaysToKeep, setCleanupDaysToKeep] = useState(30);
  const [cleanupDryRun, setCleanupDryRun] = useState(true);
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Super Admin Management State
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loadingSuperAdmins, setLoadingSuperAdmins] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);

  // Audit & Compliance State
  const [auditSummary, setAuditSummary] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [auditPeriod, setAuditPeriod] = useState(7);

  // Announcements State
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [announcementDialog, setAnnouncementDialog] = useState({ open: false, editing: null });
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    type: "info",
    expires_at: ""
  });
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);

  // AI Insights Management State
  const [allInsights, setAllInsights] = useState([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightsFilter, setInsightsFilter] = useState("all"); // all, draft, approved
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [editingInsightText, setEditingInsightText] = useState("");
  const [generatingInsight, setGeneratingInsight] = useState(null);
  const [savingInsight, setSavingInsight] = useState(false);

  // SEO Context State
  const [seoContextTenant, setSeoContextTenant] = useState(null);
  const [seoContext, setSeoContext] = useState({
    industry: "",
    recent_events: [],
    core_pages: [],
    notes: ""
  });
  const [loadingSeoContext, setLoadingSeoContext] = useState(false);
  const [savingSeoContext, setSavingSeoContext] = useState(false);
  const [newEvent, setNewEvent] = useState({ date: "", event: "", impact: "" });
  const [newCorePage, setNewCorePage] = useState("");

  // Page Notes State
  const [pageNotesTab, setPageNotesTab] = useState("notes"); // "notes" or "market-insights"
  const [pageNotesTenant, setPageNotesTenant] = useState(null);
  const [pageNotes, setPageNotes] = useState([]);
  const [marketInsights, setMarketInsights] = useState([]);
  const [loadingPageNotes, setLoadingPageNotes] = useState(false);
  const [savingPageNote, setSavingPageNote] = useState(false);
  const [selectedPageNote, setSelectedPageNote] = useState(null);
  const [pageNoteForm, setPageNoteForm] = useState({
    page_id: "",
    title: "",
    content: "",
    is_visible: true
  });
  const [marketInsightForm, setMarketInsightForm] = useState({
    page_id: "",
    insight_text: "",
    market_context: "",
    status: "draft"
  });
  const [generatingMarketInsight, setGeneratingMarketInsight] = useState(false);

  // Valid page IDs
  const VALID_PAGE_IDS = [
    { id: "dashboard", label: "Dashboard" },
    { id: "website_traffic", label: "Website Traffic" },
    { id: "social", label: "Social Media" },
    { id: "mql_sql", label: "MQL/SQL" },
    { id: "deals", label: "Deals" },
    { id: "keyword_rankings", label: "Keyword Rankings" },
    { id: "custom_analytics", label: "Custom Analytics" },
    { id: "anomaly_detection", label: "Anomaly Detection" }
  ];

  // Reports State
  const [reportConfigs, setReportConfigs] = useState([]);
  const [loadingReportConfigs, setLoadingReportConfigs] = useState(false);
  const [reportsTenant, setReportsTenant] = useState(null);
  const [selectedReportConfig, setSelectedReportConfig] = useState(null);
  const [reportPreview, setReportPreview] = useState(null);
  const [loadingReportPreview, setLoadingReportPreview] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [availableWidgets, setAvailableWidgets] = useState({});
  const [generatedReports, setGeneratedReports] = useState([]);
  const [loadingGeneratedReports, setLoadingGeneratedReports] = useState(false);
  const [deletingReport, setDeletingReport] = useState(null);
  const [reportConfigForm, setReportConfigForm] = useState({
    name: "",
    description: "",
    pages: [], // Array of { page_id, enabled, widgets: [{widget_id, enabled}] }
    date_range: "30d",
    include_toc: true,
    show_comparison: false,
    comparison_period: "previous"
  });
  const [editingSections, setEditingSections] = useState({});
  const [exportingCharts, setExportingCharts] = useState(false);
  const [exportingFullReport, setExportingFullReport] = useState(false);
  
  // Schedule state
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: false,
    frequency: "weekly",
    day_of_week: 1,
    day_of_month: 1,
    time_utc: "09:00",
    recipients: [],
    include_zip: true
  });
  const [newRecipient, setNewRecipient] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Feature Flags State
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loadingFeatureFlags, setLoadingFeatureFlags] = useState(false);
  const [featureFlagsCategories, setFeatureFlagsCategories] = useState([]);
  const [featureFlagsStatuses, setFeatureFlagsStatuses] = useState([]);
  const [selectedFlagTenant, setSelectedFlagTenant] = useState(null);
  const [tenantFlagOverrides, setTenantFlagOverrides] = useState({});
  const [newFlagForm, setNewFlagForm] = useState({
    feature_id: "",
    name: "",
    description: "",
    category: "analytics",
    status: "development",
    default_enabled: false,
    release_notes: ""
  });
  const [showNewFlagDialog, setShowNewFlagDialog] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  
  // Tab navigation state (for mobile)
  const [activeTab, setActiveTab] = useState("tenants");

  // Fetch tenants
  const fetchTenants = useCallback(async () => {
    setLoadingTenants(true);
    console.log("[SuperAdmin] Fetching tenants with token:", sessionToken ? `${sessionToken.substring(0, 8)}...` : "NULL");
    
    if (!sessionToken) {
      console.error("[SuperAdmin] No session token available!");
      toast.error("Not authenticated - please log in again");
      setLoadingTenants(false);
      return;
    }
    
    try {
      console.log("[SuperAdmin] Making request to:", `${API}/tenants/`);
      const response = await axios.get(`${API}/tenants/`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      console.log("[SuperAdmin] Tenants loaded:", response.data?.length || 0);
      setTenants(response.data || []);
    } catch (error) {
      console.error("[SuperAdmin] Error fetching tenants:", error);
      console.error("[SuperAdmin] Error response:", error.response?.data);
      console.error("[SuperAdmin] Error status:", error.response?.status);
      toast.error(`Failed to load tenants: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoadingTenants(false);
    }
  }, [sessionToken]);

  // Fetch platform analytics
  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API}/admin/platform-analytics`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setAnalyticsData(response.data || {});
    } catch (error) {
      console.error("Error fetching analytics:", error);
      // Use mock data if endpoint doesn't exist yet
      setAnalyticsData({
        totalUsers: tenants.reduce((sum, t) => sum + (t.users?.length || 0), 0),
        activeToday: Math.floor(Math.random() * 10) + 1,
        totalSessions: Math.floor(Math.random() * 100) + 50,
        pageViews: {
          dashboard: Math.floor(Math.random() * 500) + 100,
          mqls: Math.floor(Math.random() * 300) + 50,
          sqls: Math.floor(Math.random() * 200) + 30,
          deals: Math.floor(Math.random() * 150) + 20,
          social: Math.floor(Math.random() * 100) + 10
        },
        userActivity: []
      });
    } finally {
      setLoadingAnalytics(false);
    }
  }, [sessionToken, tenants]);

  // Debug log on mount
  useEffect(() => {
    console.log("[SuperAdmin] Component mounted");
    console.log("[SuperAdmin] sessionToken from useAuth:", sessionToken ? `${sessionToken.substring(0, 8)}...` : "NULL");
    console.log("[SuperAdmin] user from useAuth:", user?.email || "No user");
  }, [sessionToken, user]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    if (tenants.length > 0) {
      fetchAnalytics();
    }
  }, [tenants, fetchAnalytics]);

  // Load feature flags on mount
  useEffect(() => {
    fetchFeatureFlags();
    fetchFeatureFlagsMetadata();
  }, [sessionToken]);

  // Generate master password
  const generateMasterPassword = async () => {
    setGeneratingPassword(true);
    try {
      const response = await axios.post(`${API}/admin/generate-master-password`, {}, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      
      setMasterPassword(response.data.password);
      setMasterPasswordExpiry(new Date(response.data.expires_at));
      toast.success(`Master password generated! Valid for ${MASTER_PASSWORD_VALIDITY_HOURS} hours`);
    } catch (error) {
      console.error("Error generating master password:", error);
      // Generate locally as fallback
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
      let password = '';
      for (let i = 0; i < MASTER_PASSWORD_LENGTH; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setMasterPassword(password);
      setMasterPasswordExpiry(new Date(Date.now() + MASTER_PASSWORD_VALIDITY_HOURS * 60 * 60 * 1000));
      
      // Store in backend
      try {
        await axios.post(`${API}/admin/store-master-password`, {
          password_hash: password, // In real impl, this would be hashed
          expires_at: new Date(Date.now() + MASTER_PASSWORD_VALIDITY_HOURS * 60 * 60 * 1000).toISOString()
        }, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
      } catch (storeError) {
        console.log("Could not store master password in backend");
      }
      
      toast.success(`Master password generated! Valid for ${MASTER_PASSWORD_VALIDITY_HOURS} hours`);
    } finally {
      setGeneratingPassword(false);
    }
  };

  // Copy master password
  const copyMasterPassword = () => {
    if (masterPassword) {
      navigator.clipboard.writeText(masterPassword);
      toast.success("Master password copied to clipboard!");
    }
  };

  // Enter tenant as admin
  const enterTenant = (tenant) => {
    sessionStorage.setItem("viewing_tenant_id", tenant.tenant_id);
    sessionStorage.setItem("viewing_tenant_name", tenant.name);
    window.dispatchEvent(new Event("tenant-changed"));
    toast.success(`Now viewing as: ${tenant.name}`);
    window.location.href = "/app/dashboard";
  };

  // Login as user (impersonation)
  const loginAsUser = async (tenantUser, tenantId) => {
    try {
      // Get the target user ID
      const userId = tenantUser.user_id || tenantUser._id;
      
      const response = await axios.post(`${API}/auth/impersonate`, {
        admin_token: sessionToken,
        target_user_id: userId
      });
      
      // Extract data from response - note the backend returns nested structure
      const impersonationToken = response.data.impersonation_token;
      const targetTenantId = response.data.target_user?.tenant_id || tenantId;
      const targetTenantName = response.data.target_user?.tenant_name || "";
      
      // Store impersonation details in localStorage (App.js expects this)
      localStorage.setItem("impersonation_token", impersonationToken);
      localStorage.setItem("impersonation_original_token", sessionToken);
      localStorage.setItem("impersonation_target_email", tenantUser.email);
      localStorage.setItem("impersonation_target_name", tenantUser.name || tenantUser.email);
      localStorage.setItem("impersonation_tenant_id", targetTenantId);
      localStorage.setItem("impersonation_tenant_name", targetTenantName);
      
      // Update session for API calls - CRITICAL for tenant isolation
      sessionStorage.setItem("session_token", impersonationToken);
      sessionStorage.setItem("viewing_tenant_id", targetTenantId);
      sessionStorage.setItem("viewing_tenant_name", targetTenantName);
      
      console.log(`Impersonation: switching to tenant ${targetTenantId} (${targetTenantName})`);
      
      toast.success(`Now logged in as ${tenantUser.email}`);
      window.location.href = "/app/dashboard";
    } catch (error) {
      console.error("Error impersonating user:", error);
      toast.error(error.response?.data?.detail || "Failed to impersonate user");
    }
  };

  // Set password for user
  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSettingPassword(true);
    try {
      await axios.post(`${API}/auth/set-password`, {
        email: setPasswordDialog.user.email,
        new_password: newPassword
      }, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      
      toast.success(`Password set for ${setPasswordDialog.user.email}`);
      setSetPasswordDialog({ open: false, user: null, tenantId: null });
      setNewPassword("");
    } catch (error) {
      console.error("Error setting password:", error);
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  // Toggle GSC stats visibility for a tenant
  const toggleGscStats = async (tenantId, currentlyVisible) => {
    try {
      await axios.put(`${API}/gsc/settings`, {
        show_stats: !currentlyVisible
      }, {
        params: { 
          Authorization: `Bearer ${sessionToken}`,
          "X-Tenant-ID": tenantId
        }
      });
      
      toast.success(`GSC stats ${!currentlyVisible ? "visible" : "hidden"} for tenant`);
      
      // Refresh tenants to get updated settings
      fetchTenants();
    } catch (error) {
      console.error("Error toggling GSC stats:", error);
      toast.error("Failed to update GSC settings");
    }
  };

  // Sync data for tenant
  const syncTenantData = async (tenantId, syncType) => {
    setSyncing(prev => ({ ...prev, [`${tenantId}-${syncType}`]: true }));
    try {
      await axios.post(`${API}/integrations/hubspot/sync`, {
        sync_type: syncType
      }, {
        params: { 
          Authorization: `Bearer ${sessionToken}`,
          "X-Tenant-ID": tenantId
        }
      });
      
      toast.success(`${syncType} sync started for tenant`);
      
      // Update sync status
      setSyncStatus(prev => ({
        ...prev,
        [tenantId]: {
          ...prev[tenantId],
          [syncType]: {
            last_sync: new Date().toISOString(),
            status: "completed"
          }
        }
      }));
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error(`Failed to sync ${syncType}`);
    } finally {
      setSyncing(prev => ({ ...prev, [`${tenantId}-${syncType}`]: false }));
    }
  };

  // Toggle tenant expansion
  const toggleTenantExpansion = (tenantId) => {
    setExpandedTenants(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };

  // Fetch sync logs statistics
  const fetchSyncLogsStats = async () => {
    setLoadingSyncLogs(true);
    try {
      const response = await axios.get(`${API}/admin/sync-logs/stats`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setSyncLogsStats(response.data);
    } catch (error) {
      console.error("Error fetching sync logs stats:", error);
      toast.error("Failed to load sync logs statistics");
    } finally {
      setLoadingSyncLogs(false);
    }
  };

  // Cleanup old sync logs
  const cleanupSyncLogs = async () => {
    setCleanupInProgress(true);
    setCleanupResult(null);
    try {
      const response = await axios.delete(`${API}/admin/sync-logs/cleanup`, {
        params: { 
          Authorization: `Bearer ${sessionToken}`,
          days_to_keep: cleanupDaysToKeep,
          dry_run: cleanupDryRun
        }
      });
      setCleanupResult(response.data);
      
      if (!cleanupDryRun) {
        toast.success(`Deleted ${response.data.deleted_count?.toLocaleString()} sync logs`);
        fetchSyncLogsStats(); // Refresh stats
      }
    } catch (error) {
      console.error("Error cleaning up sync logs:", error);
      toast.error(error.response?.data?.detail || "Failed to cleanup sync logs");
    } finally {
      setCleanupInProgress(false);
    }
  };

  // Fetch super admins list
  const fetchSuperAdmins = async () => {
    setLoadingSuperAdmins(true);
    try {
      const response = await axios.get(`${API}/admin/super-admins`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setSuperAdmins(response.data.super_admins || []);
    } catch (error) {
      console.error("Error fetching super admins:", error);
      toast.error("Failed to load super admins");
    } finally {
      setLoadingSuperAdmins(false);
    }
  };

  // Add new super admin
  const addSuperAdmin = async () => {
    if (!newAdminEmail || !newAdminEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setAddingAdmin(true);
    try {
      await axios.post(`${API}/admin/super-admins`, {
        email: newAdminEmail
      }, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success(`Added ${newAdminEmail} as super admin`);
      setNewAdminEmail("");
      fetchSuperAdmins();
    } catch (error) {
      console.error("Error adding super admin:", error);
      toast.error(error.response?.data?.detail || "Failed to add super admin");
    } finally {
      setAddingAdmin(false);
    }
  };

  // Remove super admin
  const removeSuperAdmin = async (email) => {
    if (!confirm(`Remove ${email} as super admin?`)) return;
    
    try {
      await axios.delete(`${API}/admin/super-admins/${encodeURIComponent(email)}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success(`Removed ${email} from super admins`);
      fetchSuperAdmins();
    } catch (error) {
      console.error("Error removing super admin:", error);
      toast.error(error.response?.data?.detail || "Failed to remove super admin");
    }
  };

  // Fetch audit summary
  const fetchAuditSummary = useCallback(async () => {
    setLoadingAudit(true);
    try {
      const response = await axios.get(`${API}/admin/audit-summary`, {
        params: { 
          Authorization: `Bearer ${sessionToken}`,
          days: auditPeriod
        }
      });
      setAuditSummary(response.data);
      setSecurityEvents(response.data.recent_security_events || []);
      setLoginHistory(response.data.recent_logins || []);
    } catch (error) {
      console.error("Error fetching audit summary:", error);
      toast.error("Failed to load audit data");
    } finally {
      setLoadingAudit(false);
    }
  }, [sessionToken, auditPeriod]);

  // Filter tenants
  const filteredTenants = tenants.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tenant_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get integration status badge
  const getIntegrationBadge = (integration) => {
    if (integration?.connected) {
      return <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">Connected</Badge>;
    }
    return <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">Not Connected</Badge>;
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Calculate master password remaining time
  const getMasterPasswordRemainingTime = () => {
    if (!masterPasswordExpiry) return null;
    const now = new Date();
    const diffMs = masterPasswordExpiry - now;
    if (diffMs <= 0) return "Expired";
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m remaining`;
  };

  // Fetch announcements
  const fetchAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const response = await axios.get(`${API}/announcements`, {
        params: { Authorization: `Bearer ${sessionToken}`, include_expired: true }
      });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to load announcements");
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  // Save announcement (create or update)
  const saveAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) {
      toast.error("Title and content are required");
      return;
    }
    
    setSavingAnnouncement(true);
    try {
      if (announcementDialog.editing) {
        await axios.put(
          `${API}/announcements/${announcementDialog.editing.announcement_id}`,
          newAnnouncement,
          { params: { Authorization: `Bearer ${sessionToken}` } }
        );
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/announcements`, newAnnouncement, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
        toast.success("Announcement created");
      }
      setAnnouncementDialog({ open: false, editing: null });
      setNewAnnouncement({ title: "", content: "", type: "info", expires_at: "" });
      fetchAnnouncements();
    } catch (error) {
      console.error("Error saving announcement:", error);
      toast.error(error.response?.data?.detail || "Failed to save announcement");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  // Delete announcement
  const deleteAnnouncement = async (announcementId) => {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    
    try {
      await axios.delete(`${API}/announcements/${announcementId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast.error("Failed to delete announcement");
    }
  };

  // Toggle announcement active status
  const toggleAnnouncementActive = async (announcement) => {
    try {
      await axios.put(
        `${API}/announcements/${announcement.announcement_id}`,
        { is_active: !announcement.is_active },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(announcement.is_active ? "Announcement deactivated" : "Announcement activated");
      fetchAnnouncements();
    } catch (error) {
      console.error("Error toggling announcement:", error);
      toast.error("Failed to update announcement");
    }
  };

  // ============== AI INSIGHTS MANAGEMENT ==============
  
  // Fetch all insights across all tenants
  const fetchAllInsights = useCallback(async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.get(`${API}/ai-analytics/insights/all`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setAllInsights(response.data.insights || []);
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast.error("Failed to load insights");
    } finally {
      setLoadingInsights(false);
    }
  }, [sessionToken]);

  // Generate insight for a goal
  const generateInsightForGoal = async (goalId, tenantId) => {
    setGeneratingInsight(goalId);
    try {
      const response = await axios.post(
        `${API}/ai-analytics/goals/${goalId}/insights`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}`, "X-Tenant-ID": tenantId } }
      );
      toast.success("Insight generated - review and approve to publish");
      fetchAllInsights();
      setSelectedInsight(response.data);
      setEditingInsightText(response.data.insight || "");
    } catch (error) {
      console.error("Error generating insight:", error);
      toast.error("Failed to generate insight");
    } finally {
      setGeneratingInsight(null);
    }
  };

  // Save/update insight
  const saveInsight = async (goalId, tenantId, updates) => {
    if (!tenantId) {
      console.error("ERROR: No tenant_id provided for saving insight!");
      toast.error("Cannot save - tenant context missing");
      return;
    }
    
    setSavingInsight(true);
    try {
      console.log("Saving insight:", { goalId, tenantId, updates });
      // X-Tenant-ID is a query parameter (not a header) as per backend endpoint definition
      const response = await axios.patch(
        `${API}/ai-analytics/goals/${goalId}/insights`,
        updates,
        { params: { Authorization: `Bearer ${sessionToken}`, "X-Tenant-ID": tenantId } }
      );
      console.log("Save response:", response.data);
      
      if (updates.status === "approved") {
        toast.success("Insight approved and published to client");
      } else {
        toast.success("Insight saved");
      }
      
      fetchAllInsights();
      setSelectedInsight(null);
      setEditingInsightText("");
    } catch (error) {
      console.error("Error saving insight:", error);
      console.error("Error details:", error.response?.data);
      toast.error(`Failed to save insight: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSavingInsight(false);
    }
  };

  // SEO Context functions
  const fetchSeoContext = async (tenantId) => {
    setLoadingSeoContext(true);
    try {
      const response = await axios.get(
        `${API}/ai-analytics/tenants/${tenantId}/seo-context`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      setSeoContext(response.data.seo_context || {
        industry: "",
        recent_events: [],
        core_pages: [],
        notes: ""
      });
    } catch (error) {
      console.error("Error fetching SEO context:", error);
      toast.error("Failed to load SEO context");
    } finally {
      setLoadingSeoContext(false);
    }
  };

  const saveSeoContext = async () => {
    if (!seoContextTenant) return;
    setSavingSeoContext(true);
    try {
      await axios.put(
        `${API}/ai-analytics/tenants/${seoContextTenant.tenant_id}/seo-context`,
        seoContext,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("SEO context saved successfully");
    } catch (error) {
      console.error("Error saving SEO context:", error);
      toast.error("Failed to save SEO context");
    } finally {
      setSavingSeoContext(false);
    }
  };

  const addEvent = () => {
    if (newEvent.date && newEvent.event) {
      setSeoContext(prev => ({
        ...prev,
        recent_events: [...(prev.recent_events || []), { ...newEvent }]
      }));
      setNewEvent({ date: "", event: "", impact: "" });
    }
  };

  const removeEvent = (index) => {
    setSeoContext(prev => ({
      ...prev,
      recent_events: prev.recent_events.filter((_, i) => i !== index)
    }));
  };

  const addCorePage = () => {
    if (newCorePage.trim()) {
      setSeoContext(prev => ({
        ...prev,
        core_pages: [...(prev.core_pages || []), newCorePage.trim()]
      }));
      setNewCorePage("");
    }
  };

  const removeCorePage = (index) => {
    setSeoContext(prev => ({
      ...prev,
      core_pages: prev.core_pages.filter((_, i) => i !== index)
    }));
  };

  // ============== PAGE NOTES FUNCTIONS ==============
  const fetchPageNotes = async (tenantId) => {
    setLoadingPageNotes(true);
    try {
      const [notesResponse, insightsResponse] = await Promise.all([
        axios.get(`${API}/ai-analytics/tenants/${tenantId}/page-notes`, {
          params: { Authorization: `Bearer ${sessionToken}`, include_hidden: true }
        }),
        axios.get(`${API}/ai-analytics/tenants/${tenantId}/market-insights`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        })
      ]);
      setPageNotes(notesResponse.data.notes || []);
      setMarketInsights(insightsResponse.data.insights || []);
    } catch (error) {
      console.error("Error fetching page notes:", error);
      toast.error("Failed to load page notes");
    } finally {
      setLoadingPageNotes(false);
    }
  };

  const savePageNote = async () => {
    if (!pageNotesTenant || !pageNoteForm.page_id || !pageNoteForm.content) {
      toast.error("Please select a page and enter content");
      return;
    }
    
    setSavingPageNote(true);
    try {
      await axios.post(
        `${API}/ai-analytics/tenants/${pageNotesTenant.tenant_id}/page-notes`,
        pageNoteForm,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Page note saved");
      fetchPageNotes(pageNotesTenant.tenant_id);
      setPageNoteForm({ page_id: "", title: "", content: "", is_visible: true });
      setSelectedPageNote(null);
    } catch (error) {
      console.error("Error saving page note:", error);
      toast.error(error.response?.data?.detail || "Failed to save page note");
    } finally {
      setSavingPageNote(false);
    }
  };

  const deletePageNote = async (pageId) => {
    if (!pageNotesTenant) return;
    if (!confirm("Delete this page note?")) return;
    
    try {
      await axios.delete(
        `${API}/ai-analytics/tenants/${pageNotesTenant.tenant_id}/page-notes/${pageId}`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Page note deleted");
      fetchPageNotes(pageNotesTenant.tenant_id);
    } catch (error) {
      console.error("Error deleting page note:", error);
      toast.error("Failed to delete page note");
    }
  };

  const generateMarketInsight = async (pageId) => {
    if (!pageNotesTenant) return;
    
    setGeneratingMarketInsight(true);
    try {
      const response = await axios.post(
        `${API}/ai-analytics/tenants/${pageNotesTenant.tenant_id}/market-insights/generate`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}`, page_id: pageId } }
      );
      toast.success("Market insight generated - edit and approve to publish");
      fetchPageNotes(pageNotesTenant.tenant_id);
      setMarketInsightForm({
        page_id: pageId,
        insight_text: response.data.insight.insight_text,
        market_context: response.data.insight.market_context || "",
        status: "draft"
      });
    } catch (error) {
      console.error("Error generating market insight:", error);
      toast.error(error.response?.data?.detail || "Failed to generate market insight");
    } finally {
      setGeneratingMarketInsight(false);
    }
  };

  const saveMarketInsight = async () => {
    if (!pageNotesTenant || !marketInsightForm.page_id) {
      toast.error("Please select a page");
      return;
    }
    
    setSavingPageNote(true);
    try {
      await axios.patch(
        `${API}/ai-analytics/tenants/${pageNotesTenant.tenant_id}/market-insights/${marketInsightForm.page_id}`,
        {},
        { 
          params: { 
            Authorization: `Bearer ${sessionToken}`,
            insight_text: marketInsightForm.insight_text,
            market_context: marketInsightForm.market_context,
            status: marketInsightForm.status
          } 
        }
      );
      toast.success(marketInsightForm.status === "approved" ? "Market insight approved and published" : "Market insight saved");
      fetchPageNotes(pageNotesTenant.tenant_id);
    } catch (error) {
      console.error("Error saving market insight:", error);
      toast.error(error.response?.data?.detail || "Failed to save market insight");
    } finally {
      setSavingPageNote(false);
    }
  };

  const deleteMarketInsight = async (pageId) => {
    if (!pageNotesTenant) return;
    if (!confirm("Delete this market insight?")) return;
    
    try {
      await axios.delete(
        `${API}/ai-analytics/tenants/${pageNotesTenant.tenant_id}/market-insights/${pageId}`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Market insight deleted");
      fetchPageNotes(pageNotesTenant.tenant_id);
    } catch (error) {
      console.error("Error deleting market insight:", error);
      toast.error("Failed to delete market insight");
    }
  };

  // ============== REPORTS FUNCTIONS ==============
  const fetchReportConfigs = async (tenantId) => {
    setLoadingReportConfigs(true);
    try {
      // Fetch both templates and available widgets
      const [templatesRes, widgetsRes] = await Promise.all([
        axios.get(`${API}/reports/templates`, {
          params: { Authorization: `Bearer ${sessionToken}`, "X-Tenant-ID": tenantId }
        }),
        axios.get(`${API}/reports/widgets`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        })
      ]);
      setReportConfigs(templatesRes.data.templates || []);
      setAvailableWidgets(widgetsRes.data.pages || {});
    } catch (error) {
      console.error("Error fetching report configs:", error);
      toast.error("Failed to load report templates");
    } finally {
      setLoadingReportConfigs(false);
    }
  };

  const createReportConfig = async () => {
    if (!reportsTenant || !reportConfigForm.name) {
      toast.error("Please enter a report name");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/reports/templates`,
        {
          name: reportConfigForm.name,
          description: reportConfigForm.description,
          tenant_id: reportsTenant.tenant_id,
          date_range: reportConfigForm.date_range,
          pages: reportConfigForm.pages,
          include_toc: reportConfigForm.include_toc,
          show_comparison: reportConfigForm.show_comparison,
          comparison_period: reportConfigForm.comparison_period
        },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Report template created");
      fetchReportConfigs(reportsTenant.tenant_id);
      setReportConfigForm({
        name: "",
        description: "",
        pages: [],
        date_range: "30d",
        include_toc: true,
        show_comparison: false,
        comparison_period: "previous"
      });
    } catch (error) {
      console.error("Error creating report config:", error);
      toast.error(error.response?.data?.detail || "Failed to create report template");
    }
  };

  const deleteReportConfig = async (templateId) => {
    if (!confirm("Delete this report template?")) return;
    
    try {
      await axios.delete(`${API}/reports/templates/${templateId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success("Report template deleted");
      if (reportsTenant) {
        fetchReportConfigs(reportsTenant.tenant_id);
      }
      if (selectedReportConfig?.template_id === templateId) {
        setSelectedReportConfig(null);
        setReportPreview(null);
      }
    } catch (error) {
      console.error("Error deleting report config:", error);
      toast.error("Failed to delete report template");
    }
  };

  // Fetch generated reports history
  const fetchGeneratedReports = async (tenantId) => {
    setLoadingGeneratedReports(true);
    try {
      const params = { Authorization: `Bearer ${sessionToken}`, limit: 50 };
      if (tenantId) {
        params["X-Tenant-ID"] = tenantId;
      }
      const response = await axios.get(`${API}/reports/history`, { params });
      setGeneratedReports(response.data.reports || []);
    } catch (error) {
      console.error("Error fetching generated reports:", error);
    } finally {
      setLoadingGeneratedReports(false);
    }
  };

  // Delete a generated report
  const deleteGeneratedReport = async (reportId) => {
    if (!confirm("Delete this generated report?")) return;
    
    setDeletingReport(reportId);
    try {
      await axios.delete(`${API}/reports/generated/${reportId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success("Report deleted");
      fetchGeneratedReports(reportsTenant?.tenant_id);
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error(error.response?.data?.detail || "Failed to delete report");
    } finally {
      setDeletingReport(null);
    }
  };

  const previewReport = async (templateId) => {
    setLoadingReportPreview(true);
    try {
      const response = await axios.get(`${API}/reports/preview/${templateId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setReportPreview(response.data.preview);
      // Initialize editing sections with existing content
      const sections = {};
      response.data.preview.sections.forEach(s => {
        if (s.editable) {
          sections[s.section_id] = s.content || "";
        }
        // Also add editable widget text
        (s.widgets || []).forEach(w => {
          if (w.editable && w.custom_text) {
            sections[w.widget_id] = w.custom_text;
          }
        });
      });
      setEditingSections(sections);
    } catch (error) {
      console.error("Error previewing report:", error);
      toast.error(error.response?.data?.detail || "Failed to load report preview");
    } finally {
      setLoadingReportPreview(false);
    }
  };

  const generateReport = async (format = "html") => {
    if (!selectedReportConfig) return;
    
    setGeneratingReport(true);
    try {
      const response = await axios.post(
        `${API}/reports/generate`,
        {
          template_id: selectedReportConfig.template_id,
          custom_sections: editingSections,
          format
        },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.html) {
        // Open HTML in new tab
        const blob = new Blob([response.data.html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        toast.success("Report generated and opened in new tab");
      } else {
        toast.success("Report generated");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Export charts as PNG ZIP
  const exportChartsAsZip = async () => {
    if (!selectedReportConfig) return;
    
    setExportingCharts(true);
    try {
      const response = await axios.post(
        `${API}/reports/export-charts/${selectedReportConfig.template_id}`,
        {},
        { 
          params: { Authorization: `Bearer ${sessionToken}` },
          responseType: 'blob'
        }
      );
      
      // Download the ZIP file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedReportConfig.name.replace(/\s+/g, '_')}_Charts.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Charts exported as ZIP");
    } catch (error) {
      console.error("Error exporting charts:", error);
      toast.error(error.response?.data?.detail || "Failed to export charts - no chart widgets found");
    } finally {
      setExportingCharts(false);
    }
  };

  // Export full report with images
  const exportFullReport = async () => {
    if (!selectedReportConfig) return;
    
    setExportingFullReport(true);
    try {
      const response = await axios.post(
        `${API}/reports/export-full/${selectedReportConfig.template_id}`,
        {},
        { 
          params: { Authorization: `Bearer ${sessionToken}` },
          responseType: 'blob'
        }
      );
      
      // Download the ZIP file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedReportConfig.name.replace(/\s+/g, '_')}_Full_Report.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Full report with charts exported as ZIP");
    } catch (error) {
      console.error("Error exporting full report:", error);
      toast.error(error.response?.data?.detail || "Failed to export full report");
    } finally {
      setExportingFullReport(false);
    }
  };

  // Save schedule configuration
  const saveSchedule = async () => {
    if (!selectedReportConfig) return;
    
    setSavingSchedule(true);
    try {
      await axios.patch(
        `${API}/reports/templates/${selectedReportConfig.template_id}/schedule`,
        scheduleConfig,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(scheduleConfig.enabled ? "Schedule saved" : "Schedule disabled");
      fetchReportConfigs(reportsTenant?.tenant_id);
    } catch (error) {
      console.error("Error saving schedule:", error);
      toast.error("Failed to save schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    if (!selectedReportConfig || !newRecipient) return;
    
    setSendingTestEmail(true);
    try {
      await axios.post(
        `${API}/reports/send-test-email/${selectedReportConfig.template_id}`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}`, test_email: newRecipient } }
      );
      toast.success(`Test email sent to ${newRecipient}`);
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error(error.response?.data?.detail || "Failed to send test email - check SMTP configuration");
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Add recipient to schedule
  const addRecipient = () => {
    if (!newRecipient || !newRecipient.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (scheduleConfig.recipients.includes(newRecipient)) {
      toast.error("Email already added");
      return;
    }
    setScheduleConfig(prev => ({
      ...prev,
      recipients: [...prev.recipients, newRecipient]
    }));
    setNewRecipient("");
  };

  // Remove recipient
  const removeRecipient = (email) => {
    setScheduleConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  // Load schedule when report config is selected
  useEffect(() => {
    if (selectedReportConfig?.schedule) {
      setScheduleConfig(selectedReportConfig.schedule);
    } else {
      setScheduleConfig({
        enabled: false,
        frequency: "weekly",
        day_of_week: 1,
        day_of_month: 1,
        time_utc: "09:00",
        recipients: [],
        include_zip: true
      });
    }
  }, [selectedReportConfig]);

  const togglePageInConfig = (pageId, enabled) => {
    setReportConfigForm(prev => {
      const existingPage = prev.pages.find(p => p.page_id === pageId);
      if (existingPage) {
        return {
          ...prev,
          pages: prev.pages.map(p => p.page_id === pageId ? { ...p, enabled } : p)
        };
      } else {
        // Add page with all widgets enabled
        const pageWidgets = availableWidgets[pageId]?.widgets || [];
        return {
          ...prev,
          pages: [...prev.pages, {
            page_id: pageId,
            enabled,
            widgets: pageWidgets.map(w => ({ widget_id: w.id, enabled: true }))
          }]
        };
      }
    });
  };

  const toggleWidgetInConfig = (pageId, widgetId, enabled) => {
    setReportConfigForm(prev => {
      return {
        ...prev,
        pages: prev.pages.map(p => {
          if (p.page_id === pageId) {
            const existingWidget = p.widgets?.find(w => w.widget_id === widgetId);
            if (existingWidget) {
              return {
                ...p,
                widgets: p.widgets.map(w => w.widget_id === widgetId ? { ...w, enabled } : w)
              };
            } else {
              return {
                ...p,
                widgets: [...(p.widgets || []), { widget_id: widgetId, enabled }]
              };
            }
          }
          return p;
        })
      };
    });
  };

  // Drag & drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle widget reorder via drag & drop
  const handleWidgetDragEnd = (event, pageId, widgets) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = widgets.findIndex(w => w.id === active.id);
      const newIndex = widgets.findIndex(w => w.id === over.id);
      
      const reorderedWidgets = arrayMove(widgets, oldIndex, newIndex);
      
      // Update the selected report config with new widget order
      if (selectedReportConfig) {
        const updatedPages = (selectedReportConfig.pages || []).map(p => {
          if (p.page_id === pageId) {
            // Preserve enabled status while reordering
            const newWidgetConfigs = reorderedWidgets.map(w => {
              const existingConfig = p.widgets?.find(wc => wc.widget_id === w.id);
              return existingConfig || { widget_id: w.id, enabled: true };
            });
            return { ...p, widgets: newWidgetConfigs };
          }
          return p;
        });
        
        // Also add page if not exists
        const pageExists = updatedPages.some(p => p.page_id === pageId);
        if (!pageExists) {
          const newWidgetConfigs = reorderedWidgets.map(w => ({ widget_id: w.id, enabled: true }));
          updatedPages.push({ page_id: pageId, enabled: true, widgets: newWidgetConfigs });
        }
        
        setSelectedReportConfig({ ...selectedReportConfig, pages: updatedPages });
        
        // Also update reportConfigForm for consistency
        setReportConfigForm(prev => ({ ...prev, pages: updatedPages }));
        
        // Save the reordered config
        updateReportTemplateOrder(selectedReportConfig.template_id, updatedPages);
      }
    }
  };

  // Save widget order to backend
  const updateReportTemplateOrder = async (templateId, pages) => {
    try {
      await axios.patch(
        `${API}/reports/templates/${templateId}`,
        { pages },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Widget order saved");
    } catch (error) {
      console.error("Error saving widget order:", error);
      toast.error("Failed to save widget order");
    }
  };

  // ============== FEATURE FLAGS FUNCTIONS ==============
  
  const fetchFeatureFlags = async () => {
    setLoadingFeatureFlags(true);
    try {
      const response = await axios.get(`${API}/feature-flags`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setFeatureFlags(response.data.flags || []);
    } catch (error) {
      console.error("Error fetching feature flags:", error);
      toast.error("Failed to load feature flags");
    } finally {
      setLoadingFeatureFlags(false);
    }
  };

  const fetchFeatureFlagsMetadata = async () => {
    try {
      const response = await axios.get(`${API}/feature-flags/categories`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setFeatureFlagsCategories(response.data.categories || []);
      setFeatureFlagsStatuses(response.data.statuses || []);
    } catch (error) {
      console.error("Error fetching feature flags metadata:", error);
    }
  };

  const fetchTenantOverrides = async (tenantId) => {
    try {
      const response = await axios.get(`${API}/feature-flags/tenant/${tenantId}/overrides`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setTenantFlagOverrides(response.data.overrides || {});
    } catch (error) {
      console.error("Error fetching tenant overrides:", error);
    }
  };

  const createFeatureFlag = async () => {
    if (!newFlagForm.feature_id || !newFlagForm.name) {
      toast.error("Feature ID and Name are required");
      return;
    }
    
    // Convert feature_id to snake_case
    const featureId = newFlagForm.feature_id.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    
    setSavingFlag(true);
    try {
      await axios.post(`${API}/feature-flags`, {
        ...newFlagForm,
        feature_id: featureId
      }, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success("Feature flag created");
      setShowNewFlagDialog(false);
      setNewFlagForm({
        feature_id: "",
        name: "",
        description: "",
        category: "analytics",
        status: "development",
        default_enabled: false,
        release_notes: ""
      });
      fetchFeatureFlags();
    } catch (error) {
      console.error("Error creating feature flag:", error);
      toast.error(error.response?.data?.detail || "Failed to create feature flag");
    } finally {
      setSavingFlag(false);
    }
  };

  const updateFeatureFlag = async (featureId, updates) => {
    try {
      await axios.patch(`${API}/feature-flags/${featureId}`, updates, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success("Feature flag updated");
      fetchFeatureFlags();
      setEditingFlag(null);
    } catch (error) {
      console.error("Error updating feature flag:", error);
      toast.error(error.response?.data?.detail || "Failed to update feature flag");
    }
  };

  const deleteFeatureFlag = async (featureId) => {
    if (!confirm(`Delete feature flag "${featureId}"? This will also remove all tenant overrides.`)) return;
    
    try {
      await axios.delete(`${API}/feature-flags/${featureId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success("Feature flag deleted");
      fetchFeatureFlags();
    } catch (error) {
      console.error("Error deleting feature flag:", error);
      toast.error(error.response?.data?.detail || "Failed to delete feature flag");
    }
  };

  const setTenantOverride = async (featureId, enabled) => {
    if (!selectedFlagTenant) return;
    
    try {
      await axios.patch(
        `${API}/feature-flags/tenant/${selectedFlagTenant.tenant_id}/override`,
        { feature_id: featureId, enabled },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(enabled === null ? "Override removed" : `Override set to ${enabled}`);
      fetchTenantOverrides(selectedFlagTenant.tenant_id);
      fetchFeatureFlags(); // Refresh override counts
    } catch (error) {
      console.error("Error setting override:", error);
      toast.error(error.response?.data?.detail || "Failed to set override");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "development": return "bg-slate-100 text-slate-700 border-slate-300";
      case "beta": return "bg-amber-100 text-amber-700 border-amber-300";
      case "stable": return "bg-green-100 text-green-700 border-green-300";
      case "deprecated": return "bg-red-100 text-red-700 border-red-300";
      default: return "bg-slate-100 text-slate-700 border-slate-300";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "deals": return "💰";
      case "reports": return "📊";
      case "ai": return "🤖";
      case "analytics": return "📈";
      case "social": return "📱";
      case "seo": return "🔍";
      case "goals": return "🎯";
      case "traffic": return "🌐";
      default: return "⚙️";
    }
  };

  // Filter insights based on status
  const filteredInsights = allInsights.filter(insight => {
    if (insightsFilter === "all") return true;
    return insight.status === insightsFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-600" />
              Platform Owner Panel
            </h1>
            <p className="text-slate-500 mt-1">Manage tenants, users, and platform settings</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300 px-3 py-1">
              {user?.email}
            </Badge>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Tenants</p>
                  <p className="text-2xl font-bold text-slate-900">{tenants.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900">{analyticsData.totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Active Today</p>
                  <p className="text-2xl font-bold text-slate-900">{analyticsData.activeToday}</p>
                </div>
                <Activity className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Sessions</p>
                  <p className="text-2xl font-bold text-slate-900">{analyticsData.totalSessions}</p>
                </div>
                <MousePointer className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Mobile Tab Selector */}
          <div className="md:hidden">
            <Select
              value={activeTab}
              onValueChange={(value) => {
                setActiveTab(value);
                // Call fetchAllInsights if AI Insights tab selected
                if (value === "ai-insights") fetchAllInsights();
              }}
            >
              <SelectTrigger className="w-full bg-white border-slate-200">
                <SelectValue placeholder="Select section..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tenants">🏢 Tenants</SelectItem>
                <SelectItem value="security">🔐 Security</SelectItem>
                <SelectItem value="audit">🛡️ Audit & Compliance</SelectItem>
                <SelectItem value="analytics">📊 Platform Analytics</SelectItem>
                <SelectItem value="sync">🔄 Data Sync</SelectItem>
                <SelectItem value="announcements">📝 Announcements</SelectItem>
                <SelectItem value="maintenance">💾 DB Maintenance</SelectItem>
                <SelectItem value="resources">📚 Resources</SelectItem>
                <SelectItem value="feature-controls">⚙️ Feature Controls</SelectItem>
                <SelectItem value="ai-insights">🧠 AI Insights</SelectItem>
                <SelectItem value="page-notes">📄 Page Notes</SelectItem>
                <SelectItem value="seo-context">🌐 SEO Context</SelectItem>
                <SelectItem value="reports">📈 Reports</SelectItem>
                <SelectItem value="feature-flags">🚩 Feature Flags</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop Tabs - horizontally scrollable */}
          <TabsList className="hidden md:flex bg-white border border-slate-200 overflow-x-auto max-w-full">
            <TabsTrigger data-testid="tab-tenants" value="tenants" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <Building2 className="w-4 h-4 mr-2" />
              Tenants
            </TabsTrigger>
            <TabsTrigger data-testid="tab-security" value="security" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <Key className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger data-testid="tab-audit" value="audit" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <Shield className="w-4 h-4 mr-2" />
              Audit
            </TabsTrigger>
            <TabsTrigger data-testid="tab-analytics" value="analytics" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger data-testid="tab-sync" value="sync" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync
            </TabsTrigger>
            <TabsTrigger data-testid="tab-announcements" value="announcements" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <FileText className="w-4 h-4 mr-2" />
              Announce
            </TabsTrigger>
            <TabsTrigger data-testid="tab-maintenance" value="maintenance" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <Database className="w-4 h-4 mr-2" />
              DB
            </TabsTrigger>
            <TabsTrigger data-testid="tab-resources" value="resources" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <BookOpen className="w-4 h-4 mr-2" />
              Resources
            </TabsTrigger>
            <TabsTrigger data-testid="tab-feature-controls" value="feature-controls" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <Settings className="w-4 h-4 mr-2" />
              Controls
            </TabsTrigger>
            <TabsTrigger data-testid="tab-ai-insights" value="ai-insights" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap" onClick={fetchAllInsights}>
              <Brain className="w-4 h-4 mr-2" />
              AI
            </TabsTrigger>
            <TabsTrigger data-testid="tab-page-notes" value="page-notes" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <FileText className="w-4 h-4 mr-2" />
              Notes
            </TabsTrigger>
            <TabsTrigger data-testid="tab-seo-context" value="seo-context" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <Globe className="w-4 h-4 mr-2" />
              SEO
            </TabsTrigger>
            <TabsTrigger data-testid="tab-reports" value="reports" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <FileBarChart className="w-4 h-4 mr-2" />
              Reports
            </TabsTrigger>
            <TabsTrigger data-testid="tab-feature-flags" value="feature-flags" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 whitespace-nowrap">
              <ToggleLeft className="w-4 h-4 mr-2" />
              Flags
            </TabsTrigger>
          </TabsList>

          {/* Tenants Tab */}
          <TabsContent value="tenants" className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900"
                />
              </div>
              <Button
                variant="outline"
                onClick={fetchTenants}
                className="border-slate-200 text-slate-600 hover:bg-slate-100"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Tenant List */}
            <div className="space-y-3">
              {loadingTenants ? (
                <div className="text-center py-8 text-slate-500">Loading tenants...</div>
              ) : filteredTenants.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No tenants found</div>
              ) : (
                filteredTenants.map((tenant) => (
                  <Card 
                    key={tenant.tenant_id} 
                    className={`bg-white border-slate-200 overflow-hidden transition-all ${
                      tenant.is_demo ? 'border-l-4 border-l-yellow-500' : ''
                    }`}
                  >
                    {/* Tenant Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => toggleTenantExpansion(tenant.tenant_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedTenants[tenant.tenant_id] ? (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          )}
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                              {tenant.name}
                              {tenant.is_demo && (
                                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                                  DEMO
                                </Badge>
                              )}
                            </h3>
                            <p className="text-sm text-slate-500 font-mono">{tenant.tenant_id}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Integration Status Pills */}
                          <div className="flex items-center gap-2">
                            {tenant.integrations?.hubspot?.connected && (
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                                HubSpot
                              </Badge>
                            )}
                            {tenant.integrations?.linkedin?.connected && (
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                                LinkedIn
                              </Badge>
                            )}
                            {tenant.integrations?.ga4?.connected && (
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">
                                GA4
                              </Badge>
                            )}
                          </div>
                          
                          {/* Quick Actions */}
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-300 text-purple-600 hover:bg-purple-50"
                              onClick={() => enterTenant(tenant)}
                            >
                              <LogIn className="w-4 h-4 mr-1" />
                              Enter
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedTenants[tenant.tenant_id] && (
                      <div className="border-t border-slate-200 p-4 bg-slate-50/30">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Integrations */}
                          <div>
                            <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                              <Zap className="w-4 h-4" />
                              Integrations
                            </h4>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between p-2 rounded bg-slate-100">
                                <span className="text-slate-600">HubSpot</span>
                                {getIntegrationBadge(tenant.integrations?.hubspot)}
                              </div>
                              <div className="flex items-center justify-between p-2 rounded bg-slate-100">
                                <span className="text-slate-600">LinkedIn</span>
                                {getIntegrationBadge(tenant.integrations?.linkedin)}
                              </div>
                              <div className="flex items-center justify-between p-2 rounded bg-slate-100">
                                <span className="text-slate-600">Google Analytics</span>
                                {getIntegrationBadge(tenant.integrations?.ga4)}
                              </div>
                              <div className="flex items-center justify-between p-2 rounded bg-slate-100">
                                <span className="text-slate-600">Google Ads</span>
                                {getIntegrationBadge(tenant.integrations?.google_ads)}
                              </div>
                              <div className="flex items-center justify-between p-2 rounded bg-slate-100">
                                <span className="text-slate-600">Google Search Console</span>
                                {getIntegrationBadge(tenant.gsc_config?.site_url ? { connected: true } : null)}
                              </div>
                            </div>
                          </div>

                          {/* Users */}
                          <div>
                            <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              Users ({tenant.users?.length || 0})
                            </h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {tenant.users?.length > 0 ? (
                                tenant.users.map((tenantUser, idx) => (
                                  <div 
                                    key={idx}
                                    className="flex items-center justify-between p-2 rounded bg-slate-100"
                                  >
                                    <div>
                                      <p className="text-slate-600 text-sm">{tenantUser.email}</p>
                                      <p className="text-slate-500 text-xs">{tenantUser.role || 'user'}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-slate-500 hover:text-slate-900 hover:bg-slate-200"
                                        onClick={() => setSetPasswordDialog({
                                          open: true,
                                          user: tenantUser,
                                          tenantId: tenant.tenant_id
                                        })}
                                        title="Set Password"
                                      >
                                        <Key className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50"
                                        onClick={() => loginAsUser(tenantUser, tenant.tenant_id)}
                                        title="Login as User"
                                      >
                                        <LogIn className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-slate-500 text-sm">No users found</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Quick Sync Actions */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Quick Sync
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-300 text-slate-600 hover:bg-slate-100"
                              disabled={syncing[`${tenant.tenant_id}-contacts`] || !tenant.integrations?.hubspot?.connected}
                              onClick={() => syncTenantData(tenant.tenant_id, 'contacts')}
                            >
                              {syncing[`${tenant.tenant_id}-contacts`] ? (
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Database className="w-3 h-3 mr-1" />
                              )}
                              Sync Contacts
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-300 text-slate-600 hover:bg-slate-100"
                              disabled={syncing[`${tenant.tenant_id}-deals`] || !tenant.integrations?.hubspot?.connected}
                              onClick={() => syncTenantData(tenant.tenant_id, 'deals')}
                            >
                              {syncing[`${tenant.tenant_id}-deals`] ? (
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <FileText className="w-3 h-3 mr-1" />
                              )}
                              Sync Deals
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-300 text-slate-600 hover:bg-slate-100"
                              disabled={syncing[`${tenant.tenant_id}-deal_stages`] || !tenant.integrations?.hubspot?.connected}
                              onClick={() => syncTenantData(tenant.tenant_id, 'deal_stages')}
                            >
                              {syncing[`${tenant.tenant_id}-deal_stages`] ? (
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Settings className="w-3 h-3 mr-1" />
                              )}
                              Sync Deal Stages
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-500" />
                  Master Password
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Generate a temporary master password for emergency access to any account.
                  Valid for {MASTER_PASSWORD_VALIDITY_HOURS} hours after generation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {masterPassword ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-300">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-amber-700 font-medium">Active Master Password</span>
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                          <Timer className="w-3 h-3 mr-1" />
                          {getMasterPasswordRemainingTime()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-3 rounded bg-white text-emerald-600 font-mono text-lg tracking-wider border border-gray-200">
                          {masterPassword}
                        </code>
                        <Button
                          variant="outline"
                          className="border-gray-300 text-gray-600 hover:bg-gray-100"
                          onClick={copyMasterPassword}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setMasterPassword(null);
                          setMasterPasswordExpiry(null);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Revoke Password
                      </Button>
                      <Button
                        variant="outline"
                        className="border-amber-300 text-amber-600 hover:bg-amber-50"
                        onClick={generateMasterPassword}
                        disabled={generatingPassword}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${generatingPassword ? 'animate-spin' : ''}`} />
                        Regenerate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Lock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No active master password</p>
                    <Button
                      className="bg-yellow-600 hover:bg-yellow-700 text-slate-900"
                      onClick={generateMasterPassword}
                      disabled={generatingPassword}
                    >
                      {generatingPassword ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Key className="w-4 h-4 mr-2" />
                      )}
                      Generate Master Password
                    </Button>
                  </div>
                )}

                {/* Security Info */}
                <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <h4 className="text-blue-700 font-semibold mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Security Notes
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Master password is {MASTER_PASSWORD_LENGTH} characters (letters + numbers)</li>
                    <li>• Automatically expires after {MASTER_PASSWORD_VALIDITY_HOURS} hours</li>
                    <li>• Works for any user account as an emergency login</li>
                    <li>• All master password usage is logged for audit purposes</li>
                    <li>• Regenerating creates a new password and invalidates the old one</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Super Admin Management */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Super Admin Management
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Add or remove users with super admin access. Bootstrap admins (from config) cannot be removed here.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Super Admin */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email to add as super admin..."
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    className="flex-1 bg-white border-slate-300 text-slate-900"
                    onKeyDown={(e) => e.key === 'Enter' && addSuperAdmin()}
                  />
                  <Button
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={addSuperAdmin}
                    disabled={addingAdmin || !newAdminEmail}
                  >
                    {addingAdmin ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Add
                  </Button>
                </div>

                {/* Super Admins List */}
                <div className="space-y-2">
                  {superAdmins.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">Click below to load super admins</p>
                      <Button
                        variant="outline"
                        className="mt-2 border-slate-300 text-slate-600"
                        onClick={fetchSuperAdmins}
                        disabled={loadingSuperAdmins}
                      >
                        {loadingSuperAdmins ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Load Super Admins
                      </Button>
                    </div>
                  ) : (
                    superAdmins.map((admin, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-100 border border-slate-300">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${admin.is_bootstrap ? 'bg-yellow-400' : 'bg-green-400'}`} />
                          <div>
                            <p className="text-slate-900 font-medium">{admin.email}</p>
                            <p className="text-slate-500 text-xs">
                              {admin.name || 'No name'} • {admin.source === 'config' ? 'Bootstrap (config.py)' : 'Added via UI'}
                            </p>
                          </div>
                        </div>
                        {!admin.is_bootstrap && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeSuperAdmin(admin.email)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {admin.is_bootstrap && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            Protected
                          </Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Refresh Button */}
                {superAdmins.length > 0 && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-300 text-slate-600"
                      onClick={fetchSuperAdmins}
                      disabled={loadingSuperAdmins}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingSuperAdmins ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit & Compliance Tab */}
          <TabsContent value="audit" className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Audit Events ({auditPeriod}d)</p>
                      <p className="text-2xl font-bold text-slate-900">{auditSummary?.total_audit_logs || 0}</p>
                    </div>
                    <FileText className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Security Events</p>
                      <p className="text-2xl font-bold text-slate-900">{auditSummary?.total_security_events || 0}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Unique Users</p>
                      <p className="text-2xl font-bold text-slate-900">{auditSummary?.unique_users || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Spoofing Blocked</p>
                      <p className="text-2xl font-bold text-red-600">
                        {auditSummary?.security_by_type?.TENANT_SPOOFING || 0}
                      </p>
                    </div>
                    <Shield className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Controls Row */}
            <div className="flex items-center gap-4">
              <Select value={auditPeriod.toString()} onValueChange={(v) => setAuditPeriod(parseInt(v))}>
                <SelectTrigger className="w-40 bg-white">
                  <SelectValue placeholder="Time Period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                className="border-slate-300 text-slate-600 hover:bg-slate-100"
                onClick={fetchAuditSummary}
                disabled={loadingAudit}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingAudit ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Security Events */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Recent Security Events
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    Blocked access attempts and security alerts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {securityEvents.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Shield className="w-12 h-12 mx-auto mb-2 text-green-500" />
                      <p>No security events in this period</p>
                      <p className="text-sm">Your platform is secure</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {securityEvents.map((event, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${
                            event.severity === 'CRITICAL' ? 'bg-red-50 border-red-200' :
                            event.severity === 'WARNING' ? 'bg-amber-50 border-amber-200' :
                            'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                event.event_type === 'TENANT_SPOOFING' ? 'bg-red-100 text-red-700 border-red-300' :
                                event.event_type === 'UNAUTHORIZED_ACCESS' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                'bg-blue-100 text-blue-700 border-blue-300'
                              }`}
                            >
                              {event.event_type}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {new Date(event.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700">{event.user_email || 'Unknown user'}</p>
                          {event.details?.attempted_tenant && (
                            <p className="text-xs text-slate-500 mt-1">
                              Attempted tenant: {event.details.attempted_tenant.substring(0, 8)}...
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Logins */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Recent Logins
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    User session activity
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loginHistory.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-2" />
                      <p>No login data available</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {loginHistory.map((login, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-900">{login.email}</span>
                            <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-300">
                              {login.role || 'user'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">
                            {login.created_at ? new Date(login.created_at).toLocaleString() : 'Unknown time'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity by Tenant */}
            {auditSummary?.access_by_tenant?.length > 0 && (
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    Data Access by Tenant
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    Which tenants&apos; data is being accessed most
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {auditSummary.access_by_tenant.map((tenant, idx) => {
                      const maxCount = auditSummary.access_by_tenant[0]?.count || 1;
                      const percentage = (tenant.count / maxCount) * 100;
                      const tenantInfo = tenants.find(t => t.tenant_id === tenant._id);
                      
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">
                              {tenantInfo?.name || tenant._id?.substring(0, 8) || 'Unknown'}
                            </span>
                            <span className="text-slate-500">{tenant.count.toLocaleString()} accesses</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div 
                              className="h-full rounded-full bg-purple-500 transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SOC-3 Compliance Info */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  SOC-3 Compliance Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Data Isolation</span>
                    </div>
                    <p className="text-sm text-green-700">
                      Multi-tenant data isolation active. Users can only access authorized tenants.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Audit Logging</span>
                    </div>
                    <p className="text-sm text-green-700">
                      All data access and security events logged with timestamps and user context.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Access Control</span>
                    </div>
                    <p className="text-sm text-green-700">
                      Role-based access with tenant membership verification on every request.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Page Views Heatmap */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Page Views Heatmap
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    Most visited pages across all tenants
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analyticsData.pageViews || {}).map(([page, views]) => {
                      const maxViews = Math.max(...Object.values(analyticsData.pageViews || {1: 1}));
                      const percentage = (views / maxViews) * 100;
                      const intensity = Math.min(percentage / 100, 1);
                      
                      return (
                        <div key={page} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 capitalize">{page}</span>
                            <span className="text-slate-500">{views.toLocaleString()}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: `rgba(147, 51, 234, ${0.3 + intensity * 0.7})`
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    User logins and actions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analyticsData.userActivity?.length > 0 ? (
                      analyticsData.userActivity.slice(0, 10).map((activity, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded bg-slate-100">
                          <div className={`w-2 h-2 rounded-full ${
                            activity.type === 'login' ? 'bg-green-400' : 
                            activity.type === 'page_view' ? 'bg-blue-400' : 'bg-purple-400'
                          }`} />
                          <div className="flex-1">
                            <p className="text-sm text-slate-600">{activity.user}</p>
                            <p className="text-xs text-slate-500">{activity.action}</p>
                          </div>
                          <span className="text-xs text-slate-500">{formatTimeAgo(activity.timestamp)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Activity tracking coming soon</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tenant Health Overview */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-600" />
                  Tenant Health Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {tenants.map((tenant) => {
                    const integrationCount = Object.values(tenant.integrations || {}).filter(i => i?.connected).length;
                    const healthStatus = integrationCount >= 2 ? 'healthy' : integrationCount >= 1 ? 'warning' : 'critical';
                    
                    return (
                      <div 
                        key={tenant.tenant_id}
                        className={`p-4 rounded-lg border ${
                          healthStatus === 'healthy' ? 'border-green-300 bg-green-50' :
                          healthStatus === 'warning' ? 'border-amber-300 bg-amber-50' :
                          'border-red-300 bg-red-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-slate-900 font-medium truncate">{tenant.name}</h4>
                          {healthStatus === 'healthy' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : healthStatus === 'warning' ? (
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div className="text-xs text-slate-500 space-y-1">
                          <p>{integrationCount} integrations connected</p>
                          <p>{tenant.users?.length || 0} users</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Sync Tab */}
          <TabsContent value="sync" className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  Scheduled Sync Configuration
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Configure nightly data sync for all tenants
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-gray-600">
                    Nightly sync feature coming soon. This will automatically sync HubSpot contacts, 
                    deals, and LinkedIn metrics at a scheduled time (e.g., 2 AM) to keep data fresh 
                    and reduce API calls during business hours.
                  </p>
                </div>

                {/* Manual Bulk Sync */}
                <div className="mt-6">
                  <h4 className="text-slate-900 font-semibold mb-4">Manual Bulk Sync</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tenants.filter(t => t.integrations?.hubspot?.connected).map((tenant) => (
                      <div key={tenant.tenant_id} className="p-4 rounded-lg bg-slate-100 border border-slate-300">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-slate-900 font-medium">{tenant.name}</h5>
                          {syncStatus[tenant.tenant_id]?.contacts?.status === 'completed' && (
                            <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">
                              Synced
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-200"
                            disabled={syncing[`${tenant.tenant_id}-contacts`]}
                            onClick={() => syncTenantData(tenant.tenant_id, 'contacts')}
                          >
                            {syncing[`${tenant.tenant_id}-contacts`] ? (
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : null}
                            Contacts
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-200"
                            disabled={syncing[`${tenant.tenant_id}-deals`]}
                            onClick={() => syncTenantData(tenant.tenant_id, 'deals')}
                          >
                            {syncing[`${tenant.tenant_id}-deals`] ? (
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : null}
                            Deals
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Platform Announcements
                    </CardTitle>
                    <CardDescription className="text-slate-500">
                      Create and manage platform-wide announcements visible to all users
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setNewAnnouncement({ title: "", content: "", type: "info", expires_at: "" });
                      setAnnouncementDialog({ open: true, editing: null });
                      fetchAnnouncements();
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Announcement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingAnnouncements ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : announcements.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p>No announcements yet</p>
                    <p className="text-sm mt-1">Create your first announcement to notify users</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((announcement) => (
                      <div
                        key={announcement.announcement_id}
                        className={`border rounded-lg p-4 ${announcement.is_active ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-60"}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{announcement.title}</h4>
                              <Badge variant={announcement.type === "urgent" ? "destructive" : announcement.type === "warning" ? "warning" : "secondary"}>
                                {announcement.type}
                              </Badge>
                              {!announcement.is_active && <Badge variant="outline">Inactive</Badge>}
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2">{announcement.content.replace(/<[^>]*>/g, '')}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              Created {new Date(announcement.created_at).toLocaleDateString()} by {announcement.created_by}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleAnnouncementActive(announcement)}
                            >
                              {announcement.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setNewAnnouncement({
                                  title: announcement.title,
                                  content: announcement.content,
                                  type: announcement.type,
                                  expires_at: announcement.expires_at || ""
                                });
                                setAnnouncementDialog({ open: true, editing: announcement });
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => deleteAnnouncement(announcement.announcement_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Database Maintenance Tab */}
          <TabsContent value="maintenance" className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Database className="w-5 h-5 text-orange-600" />
                  Sync Logs Cleanup
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Clean up old sync event logs to reduce database size. This does NOT delete any cached 
                  data (contacts, deals, analytics) - only the sync event logs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Load Stats Button */}
                {!syncLogsStats && (
                  <div className="text-center py-8">
                    <Database className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Click below to analyze sync logs</p>
                    <Button
                      className="bg-orange-600 hover:bg-orange-700 text-slate-900"
                      onClick={fetchSyncLogsStats}
                      disabled={loadingSyncLogs}
                    >
                      {loadingSyncLogs ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <BarChart3 className="w-4 h-4 mr-2" />
                      )}
                      Analyze Sync Logs
                    </Button>
                  </div>
                )}

                {/* Stats Display */}
                {syncLogsStats && (
                  <>
                    {/* Overview Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-slate-100 border border-slate-300">
                        <p className="text-slate-500 text-sm">Total Logs</p>
                        <p className="text-2xl font-bold text-slate-900">{syncLogsStats.total_count?.toLocaleString()}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-amber-700 text-sm">Older than 7 days</p>
                        <p className="text-2xl font-bold text-amber-800">{syncLogsStats.age_distribution?.older_than_7_days?.toLocaleString()}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                        <p className="text-orange-700 text-sm">Older than 30 days</p>
                        <p className="text-2xl font-bold text-orange-800">{syncLogsStats.age_distribution?.older_than_30_days?.toLocaleString()}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                        <p className="text-red-700 text-sm">Older than 90 days</p>
                        <p className="text-2xl font-bold text-red-800">{syncLogsStats.age_distribution?.older_than_90_days?.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="p-4 rounded-lg bg-slate-100 border border-slate-300">
                      <h4 className="text-slate-600 font-semibold mb-2">Log Date Range</h4>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">
                          Oldest: <span className="text-slate-900">{syncLogsStats.oldest_log ? new Date(syncLogsStats.oldest_log).toLocaleDateString() : 'N/A'}</span>
                        </span>
                        <span className="text-slate-500">→</span>
                        <span className="text-slate-500">
                          Newest: <span className="text-slate-900">{syncLogsStats.newest_log ? new Date(syncLogsStats.newest_log).toLocaleDateString() : 'N/A'}</span>
                        </span>
                      </div>
                    </div>

                    {/* Logs by Tenant */}
                    <div className="p-4 rounded-lg bg-slate-100 border border-slate-300">
                      <h4 className="text-slate-600 font-semibold mb-3">Logs by Tenant</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {syncLogsStats.by_tenant?.map((t, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded bg-white">
                            <span className="text-slate-600 text-sm">{t.tenant_name || t.tenant_id || 'Unknown'}</span>
                            <span className="text-slate-500 text-sm">{t.count?.toLocaleString()} logs</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Cleanup Controls */}
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                      <h4 className="text-red-700 font-semibold mb-4 flex items-center gap-2">
                        <Trash2 className="w-4 h-4" />
                        Cleanup Settings
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <Label className="text-slate-600">Days to Keep</Label>
                          <Select value={cleanupDaysToKeep.toString()} onValueChange={(v) => setCleanupDaysToKeep(parseInt(v))}>
                            <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-50 border-slate-200">
                              <SelectItem value="7">Keep last 7 days</SelectItem>
                              <SelectItem value="14">Keep last 14 days</SelectItem>
                              <SelectItem value="30">Keep last 30 days</SelectItem>
                              <SelectItem value="60">Keep last 60 days</SelectItem>
                              <SelectItem value="90">Keep last 90 days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-slate-600">Mode</Label>
                          <Select value={cleanupDryRun ? "dry" : "delete"} onValueChange={(v) => setCleanupDryRun(v === "dry")}>
                            <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-50 border-slate-200">
                              <SelectItem value="dry">Dry Run (Preview Only)</SelectItem>
                              <SelectItem value="delete">Actually Delete</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Button
                        className={cleanupDryRun ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}
                        onClick={cleanupSyncLogs}
                        disabled={cleanupInProgress}
                      >
                        {cleanupInProgress ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        {cleanupDryRun ? "Preview Cleanup" : "Delete Old Logs"}
                      </Button>

                      {/* Cleanup Result */}
                      {cleanupResult && (
                        <div className={`mt-4 p-4 rounded-lg ${cleanupResult.dry_run ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                          {cleanupResult.dry_run ? (
                            <p className="text-amber-800">
                              <strong>Preview:</strong> Would delete <strong>{cleanupResult.would_delete?.toLocaleString()}</strong> logs 
                              older than {new Date(cleanupResult.cutoff_date).toLocaleDateString()}
                            </p>
                          ) : (
                            <p className="text-green-800">
                              <strong>Completed:</strong> Deleted <strong>{cleanupResult.deleted_count?.toLocaleString()}</strong> logs
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Refresh Button */}
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        className="border-slate-300 text-slate-600 hover:bg-slate-100"
                        onClick={fetchSyncLogsStats}
                        disabled={loadingSyncLogs}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingSyncLogs ? 'animate-spin' : ''}`} />
                        Refresh Stats
                      </Button>
                    </div>
                  </>
                )}

                {/* Safe Data Notice */}
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <h4 className="text-blue-700 font-semibold mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    What Gets Preserved
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ HubSpot contacts and deals (cached data)</li>
                    <li>✓ LinkedIn analytics and daily stats</li>
                    <li>✓ GA4 and Google Ads data</li>
                    <li>✓ User accounts and tenant settings</li>
                    <li>✓ Integration tokens and configurations</li>
                    <li className="text-red-600">✗ Only sync event logs are deleted (started, completed, error events)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-4">
            {/* Microsoft Clarity Setup */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <MousePointer className="w-5 h-5 text-blue-500" />
                  Microsoft Clarity Setup Guide
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Free heatmaps, session recordings, and scroll depth tracking for client websites
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-green-700 font-medium flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    100% Free - No traffic limits, no API limits, unlimited recordings
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800">Installation Steps:</h4>
                  
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                      <div>
                        <p className="text-slate-700 font-medium">Create Clarity Account</p>
                        <p className="text-slate-500 text-sm">Go to <a href="https://clarity.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">clarity.microsoft.com <ExternalLink className="w-3 h-3" /></a> and sign in with Microsoft account</p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                      <div>
                        <p className="text-slate-700 font-medium">Add New Project</p>
                        <p className="text-slate-500 text-sm">Click &quot;Add new project&quot; → Enter client&apos;s website URL → Create project</p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                      <div>
                        <p className="text-slate-700 font-medium">Get Tracking Code</p>
                        <p className="text-slate-500 text-sm">Copy the JavaScript snippet provided by Clarity</p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                      <div>
                        <p className="text-slate-700 font-medium">Install on Website</p>
                        <p className="text-slate-500 text-sm">Paste code in the <code className="bg-slate-100 px-1 rounded">&lt;head&gt;</code> section of every page, or use Google Tag Manager</p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">5</div>
                      <div>
                        <p className="text-slate-700 font-medium">Verify Installation</p>
                        <p className="text-slate-500 text-sm">Wait 2-3 hours, then check Clarity dashboard for incoming data</p>
                      </div>
                    </div>
                  </div>

                  {/* Code Example */}
                  <div className="mt-4">
                    <p className="text-slate-600 text-sm font-medium mb-2">Example Tracking Code:</p>
                    <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg text-xs overflow-x-auto border border-gray-200">
{`<script type="text/javascript">
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window,document,"clarity","script","YOUR_PROJECT_ID");
</script>`}
                    </pre>
                  </div>
                </div>

                {/* Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <h5 className="font-medium text-slate-700 mb-2">What You Get:</h5>
                    <ul className="text-sm text-slate-500 space-y-1">
                      <li>✓ Click heatmaps</li>
                      <li>✓ Scroll depth visualization</li>
                      <li>✓ Session recordings</li>
                      <li>✓ Rage click detection</li>
                      <li>✓ Dead click detection</li>
                      <li>✓ JavaScript error tracking</li>
                    </ul>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <h5 className="font-medium text-amber-700 mb-2">GDPR Note:</h5>
                    <p className="text-sm text-amber-600">
                      For EU visitors, implement the Clarity Consent API by Oct 31, 2025. 
                      See <a href="https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api" target="_blank" rel="noopener noreferrer" className="underline">Microsoft docs</a> for details.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Admin Notes */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-500" />
                  Admin Notes & Quick Reference
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <h5 className="font-medium text-blue-700 mb-2">Emergency Backdoor URL</h5>
                    <p className="text-sm text-blue-600 font-mono break-all">/jci-internal-7f8a9b2c4d6e1f3a5b7c9d0e2f4a6b8c</p>
                    <p className="text-xs text-blue-500 mt-2">Requires active master password</p>
                  </div>

                  <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                    <h5 className="font-medium text-purple-700 mb-2">HubSpot Sync Schedule</h5>
                    <p className="text-sm text-purple-600">Daily at midnight UTC</p>
                    <p className="text-xs text-purple-500 mt-2">Initial sync on OAuth connection</p>
                  </div>

                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <h5 className="font-medium text-green-700 mb-2">MongoDB Production</h5>
                    <p className="text-sm text-green-600">Atlas M10 Cluster</p>
                    <p className="text-xs text-green-500 mt-2">Check .env for connection string</p>
                  </div>

                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <h5 className="font-medium text-orange-700 mb-2">Sync Logs</h5>
                    <p className="text-sm text-orange-600">Use DB Maintenance tab to clean</p>
                    <p className="text-xs text-orange-500 mt-2">Keep last 30 days recommended</p>
                  </div>
                </div>
                
                {/* Goal Templates Reference */}
                <div className="mt-6 p-4 rounded-lg bg-indigo-50 border border-indigo-200">
                  <h5 className="font-medium text-indigo-700 mb-3">Goal Templates Reference</h5>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium text-indigo-800">✅ READY (Data Available):</p>
                      <ul className="text-indigo-600 ml-4 mt-1 space-y-0.5">
                        <li>• <strong>MQLs/SQLs</strong> - MongoDB: hubspot_contacts.lifecycle_stage (uses tenant stage mapping)</li>
                        <li>• <strong>MQL→SQL Rate</strong> - Calculated from above</li>
                        <li>• <strong>Form Submissions</strong> - MongoDB: form_submissions (4,039 records)</li>
                        <li>• <strong>LinkedIn Followers/Engagement/Impressions</strong> - MongoDB: linkedin_daily_metrics</li>
                        <li>• <strong>Sales Cycle Length</strong> - HubSpot: mql_date, sql_date, customer_date fields</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-amber-800">⚠️ NEEDS FIXES:</p>
                      <ul className="text-amber-600 ml-4 mt-1 space-y-0.5">
                        <li>• <strong>Deal Revenue</strong> - Must filter to Closed Won deals only (currently includes all)</li>
                        <li>• <strong>Pipeline Value</strong> - Must filter to OPEN deals only (excludes won/lost)</li>
                        <li>• <strong>Win Rate</strong> - Needs Won / (Won + Lost) calculation</li>
                        <li>• <strong>Content Downloads</strong> - Needs page/form selection UI</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-red-800">❌ NEEDS INTEGRATION:</p>
                      <ul className="text-red-600 ml-4 mt-1 space-y-0.5">
                        <li>• <strong>GA4 Required</strong>: Website Sessions, Organic Traffic, Bounce Rate, Pages/Session, Dwell Time</li>
                        <li>• <strong>GSC Required</strong>: Top 10 Search Terms (backend ready, needs OAuth)</li>
                        <li>• <strong>HubSpot Marketing Hub</strong>: Email Engagement (open/click rates)</li>
                        <li>• <strong>Manual Input</strong>: Marketing ROI (needs spend + labor costs entry page)</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">📝 Special Templates:</p>
                      <ul className="text-slate-600 ml-4 mt-1 space-y-0.5">
                        <li>• <strong>Top 10 Search Terms</strong> - Opens dedicated page: keyword exclusions, position &lt;11, 90-day window</li>
                        <li>• <strong>Marketing ROI</strong> - Opens dedicated page: spend entry, labor costs toggle</li>
                        <li>• <strong>Lead Response Time</strong> - HubSpot has hs_time_to_first_reply, needs sync addition</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">🗑️ REMOVED:</p>
                      <ul className="text-gray-500 ml-4 mt-1">
                        <li>• Keyword Rankings (duplicate of Top 10 Search Terms)</li>
                        <li>• Backlink Growth (no Semrush/Ahrefs API)</li>
                        <li>• Video Engagement (deferred)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Future Feature Ideas */}
                <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <h5 className="font-medium text-slate-700 mb-3">Feature Ideas (Parked)</h5>
                  <ul className="text-sm text-slate-500 space-y-2">
                    <li className="flex items-start gap-2">
                      <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-600 border-yellow-300">Idea</Badge>
                      <span>Custom heatmap tracking - test on JCI website first, then offer to clients</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-600 border-yellow-300">Idea</Badge>
                      <span>Embed Clarity data into j-MAP via API integration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-600 border-yellow-300">Idea</Badge>
                      <span>Gmail 2FA for emergency backdoor (14-digit code to jason@jci-marketing.com)</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Controls Tab */}
          <TabsContent value="feature-controls" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-orange-600" />
                  Google Search Console Settings
                </CardTitle>
                <CardDescription>
                  Control GSC feature visibility per tenant
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 mb-4">
                    Toggle whether tenants can see clicks, impressions, and CTR data on the GSC Keyword Rankings page.
                    When hidden, users will only see keyword count and average position.
                  </p>
                  <div className="border rounded-lg divide-y">
                    {tenants.filter(t => t.gsc_config?.site_url).length === 0 ? (
                      <div className="p-4 text-center text-slate-500">
                        No tenants have GSC connected yet
                      </div>
                    ) : (
                      tenants.filter(t => t.gsc_config?.site_url).map(tenant => (
                        <div key={tenant.tenant_id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                              <Search className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{tenant.name}</p>
                              <p className="text-sm text-slate-500">{tenant.gsc_config?.site_url}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-500">
                              {tenant.gsc_config?.show_stats !== false ? "Stats Visible" : "Stats Hidden"}
                            </span>
                            <Button
                              size="sm"
                              variant={tenant.gsc_config?.show_stats !== false ? "default" : "outline"}
                              className={tenant.gsc_config?.show_stats !== false 
                                ? "bg-green-600 hover:bg-green-700" 
                                : "text-slate-600 border-slate-300"}
                              onClick={() => toggleGscStats(tenant.tenant_id, tenant.gsc_config?.show_stats !== false)}
                            >
                              {tenant.gsc_config?.show_stats !== false ? (
                                <>
                                  <Eye className="w-4 h-4 mr-1" />
                                  Visible
                                </>
                              ) : (
                                <>
                                  <EyeOff className="w-4 h-4 mr-1" />
                                  Hidden
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="ai-insights" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-amber-600" />
                  AI Insights Review
                </CardTitle>
                <CardDescription>
                  Review, edit, and approve AI-generated insights before they are visible to clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filter Controls */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex gap-2">
                    <Button
                      variant={insightsFilter === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setInsightsFilter("all")}
                      className={insightsFilter === "all" ? "bg-purple-600 hover:bg-purple-700" : ""}
                    >
                      All
                    </Button>
                    <Button
                      variant={insightsFilter === "draft" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setInsightsFilter("draft")}
                      className={insightsFilter === "draft" ? "bg-yellow-600 hover:bg-yellow-700" : ""}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Pending Review
                    </Button>
                    <Button
                      variant={insightsFilter === "approved" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setInsightsFilter("approved")}
                      className={insightsFilter === "approved" ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Published
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchAllInsights}
                    className="ml-auto"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingInsights ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>

                {/* Insights List */}
                {loadingInsights ? (
                  <div className="text-center py-8 text-slate-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading insights...
                  </div>
                ) : filteredInsights.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Brain className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No insights found</p>
                    <p className="text-sm">Insights are generated when goals are at risk</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredInsights.map((insight) => (
                      <div
                        key={insight.insight_id || insight.goal_id}
                        className={`border rounded-lg p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                          selectedInsight?.goal_id === insight.goal_id ? "border-purple-400 bg-purple-50" : "border-slate-200"
                        }`}
                        onClick={() => {
                          console.log("Insight clicked - full object:", insight);
                          console.log("Insight tenant_id:", insight.tenant_id);
                          setSelectedInsight(insight);
                          setEditingInsightText(insight.insight_text || "");
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Building2 className="w-4 h-4 text-purple-600" />
                              <span className="font-semibold text-purple-900 text-lg">{insight.tenant_name || insight.tenant_id}</span>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  insight.status === "approved" 
                                    ? "bg-green-100 text-green-700 border-green-300" 
                                    : insight.status === "draft"
                                    ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                                    : "bg-slate-100 text-slate-700 border-slate-300"
                                }`}
                              >
                                {insight.status === "approved" ? "Published" : insight.status === "draft" ? "Pending Review" : insight.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              <span className="font-medium">Goal:</span> {insight.goal_name || insight.metric || "Unknown"}
                            </p>
                            <p className="text-xs text-slate-400">
                              Generated {insight.generated_at ? new Date(insight.generated_at).toLocaleString() : "N/A"}
                              {insight.generated_by && ` by ${insight.generated_by}`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {insight.status === "draft" && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveInsight(insight.goal_id, insight.tenant_id, { status: "approved" });
                                }}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Insight Editor Panel */}
            {selectedInsight && (
              <Card className="border-purple-200 bg-purple-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-6 h-6 text-purple-600" />
                      <span className="text-xl font-bold text-purple-900">{selectedInsight.tenant_name || selectedInsight.tenant_id}</span>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          selectedInsight.status === "approved" 
                            ? "bg-green-100 text-green-700 border-green-300" 
                            : "bg-yellow-100 text-yellow-700 border-yellow-300"
                        }`}
                      >
                        {selectedInsight.status === "approved" ? "Published" : "Pending Review"}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedInsight(null);
                        setEditingInsightText("");
                      }}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Edit className="w-4 h-4 text-purple-600" />
                    {selectedInsight.goal_name || selectedInsight.metric || "Goal Insight"}
                  </CardTitle>
                  <CardDescription>
                    Review and edit the AI-generated content before publishing
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stats Summary */}
                  {selectedInsight.stats && (
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Performance Data</h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Baseline:</span>
                          <span className="ml-2 font-medium">{selectedInsight.stats.baseline_keywords} keywords</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Current:</span>
                          <span className="ml-2 font-medium">{selectedInsight.stats.current_keywords} keywords</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Dropped:</span>
                          <span className="ml-2 font-medium text-red-600">{selectedInsight.stats.dropped}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Gained:</span>
                          <span className="ml-2 font-medium text-green-600">{selectedInsight.stats.gained}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Text Editor with Markdown Preview */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Editor Side */}
                    <div className="bg-white rounded-lg border border-purple-200">
                      <div className="border-b border-purple-100 p-2 bg-purple-50/50 flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium">Edit (Markdown)</span>
                        <Edit className="w-3 h-3 text-slate-400" />
                      </div>
                      <textarea
                        value={editingInsightText}
                        onChange={(e) => setEditingInsightText(e.target.value)}
                        className="w-full min-h-[350px] p-4 text-sm focus:outline-none resize-y font-mono"
                        placeholder="Edit the insight content..."
                      />
                    </div>
                    
                    {/* Preview Side */}
                    <div className="bg-white rounded-lg border border-green-200">
                      <div className="border-b border-green-100 p-2 bg-green-50/50 flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium">Preview (Client View)</span>
                        <Eye className="w-3 h-3 text-green-500" />
                      </div>
                      <div className="p-4 min-h-[350px] overflow-auto prose prose-sm max-w-none prose-headings:text-purple-900 prose-strong:text-slate-800 prose-ul:my-2 prose-li:my-0">
                        <ReactMarkdown
                          components={{
                            h1: ({node, ...props}) => <h1 className="text-xl font-bold text-purple-900 mb-3" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-lg font-semibold text-purple-800 mb-2 mt-4" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-base font-semibold text-slate-700 mb-2 mt-3" {...props} />,
                            p: ({node, ...props}) => <p className="text-sm text-slate-600 mb-2" {...props} />,
                            strong: ({node, ...props}) => <strong className="font-semibold text-slate-800" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
                            li: ({node, ...props}) => <li className="text-sm text-slate-600" {...props} />,
                            table: ({node, ...props}) => <table className="min-w-full text-sm border-collapse mb-3" {...props} />,
                            th: ({node, ...props}) => <th className="border border-slate-200 px-2 py-1 bg-slate-50 font-medium text-left" {...props} />,
                            td: ({node, ...props}) => <td className="border border-slate-200 px-2 py-1" {...props} />,
                            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-amber-400 pl-3 italic text-amber-700 bg-amber-50 py-1 my-2" {...props} />,
                          }}
                        >
                          {editingInsightText || "*No content yet*"}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => saveInsight(selectedInsight.goal_id, selectedInsight.tenant_id, { insight_text: editingInsightText })}
                      disabled={savingInsight}
                    >
                      {savingInsight && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                      Save Draft
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        console.log("Save & Publish clicked:", {
                          goalId: selectedInsight.goal_id,
                          tenantId: selectedInsight.tenant_id,
                          tenantName: selectedInsight.tenant_name
                        });
                        saveInsight(selectedInsight.goal_id, selectedInsight.tenant_id, { 
                          insight_text: editingInsightText, 
                          status: "approved" 
                        });
                      }}
                      disabled={savingInsight}
                    >
                      {savingInsight && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save & Publish
                    </Button>
                    {selectedInsight.status === "approved" && (
                      <Button
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={() => saveInsight(selectedInsight.goal_id, selectedInsight.tenant_id, { status: "draft" })}
                        disabled={savingInsight}
                      >
                        Unpublish
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Page Notes Tab */}
          <TabsContent value="page-notes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Page Notes & Market Insights
                </CardTitle>
                <CardDescription>
                  Add custom notes to any page for any tenant. Notes appear at the top of pages and support markdown formatting.
                  Market insights provide AI-generated context about industry trends.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Tenant Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Select Tenant</label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={pageNotesTenant?.tenant_id || ""}
                    onChange={(e) => {
                      const tenant = tenants.find(t => t.tenant_id === e.target.value);
                      setPageNotesTenant(tenant);
                      if (tenant) {
                        fetchPageNotes(tenant.tenant_id);
                      }
                    }}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {pageNotesTenant && (
                  <div className="space-y-4">
                    {/* Sub-tabs for Notes vs Market Insights */}
                    <div className="flex gap-2 border-b border-slate-200 pb-2">
                      <Button
                        variant={pageNotesTab === "notes" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPageNotesTab("notes")}
                        className={pageNotesTab === "notes" ? "bg-purple-600" : ""}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Custom Notes
                      </Button>
                      <Button
                        variant={pageNotesTab === "market-insights" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPageNotesTab("market-insights")}
                        className={pageNotesTab === "market-insights" ? "bg-purple-600" : ""}
                      >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Market Insights
                      </Button>
                    </div>

                    {pageNotesTab === "notes" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Existing Notes List */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-slate-700">Existing Notes</h4>
                          {loadingPageNotes ? (
                            <div className="text-center py-4 text-slate-500">Loading...</div>
                          ) : pageNotes.length === 0 ? (
                            <div className="text-center py-4 text-slate-500">No notes yet</div>
                          ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {pageNotes.map(note => (
                                <div
                                  key={`${note.tenant_id}-${note.page_id}`}
                                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                    selectedPageNote?.page_id === note.page_id
                                      ? "border-purple-500 bg-purple-50"
                                      : "border-slate-200 hover:border-purple-300"
                                  }`}
                                  onClick={() => {
                                    setSelectedPageNote(note);
                                    setPageNoteForm({
                                      page_id: note.page_id,
                                      title: note.title || "",
                                      content: note.content || "",
                                      is_visible: note.is_visible !== false
                                    });
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">
                                          {VALID_PAGE_IDS.find(p => p.id === note.page_id)?.label || note.page_id}
                                        </Badge>
                                        {!note.is_visible && (
                                          <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                                            Hidden
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-600 mt-1 line-clamp-1">
                                        {note.title || note.content?.substring(0, 50)}
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deletePageNote(note.page_id);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Note Editor */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-slate-700">
                            {selectedPageNote ? "Edit Note" : "Add New Note"}
                          </h4>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs text-slate-500">Page</label>
                              <select
                                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                                value={pageNoteForm.page_id}
                                onChange={(e) => setPageNoteForm(prev => ({ ...prev, page_id: e.target.value }))}
                              >
                                <option value="">-- Select page --</option>
                                {VALID_PAGE_IDS.map(page => (
                                  <option key={page.id} value={page.id}>{page.label}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="text-xs text-slate-500">Title (optional)</label>
                              <Input
                                value={pageNoteForm.title}
                                onChange={(e) => setPageNoteForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Note title"
                              />
                            </div>
                            
                            <div>
                              <label className="text-xs text-slate-500">Content (Markdown supported)</label>
                              <Textarea
                                value={pageNoteForm.content}
                                onChange={(e) => setPageNoteForm(prev => ({ ...prev, content: e.target.value }))}
                                placeholder="Enter note content..."
                                rows={6}
                              />
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="note-visible"
                                checked={pageNoteForm.is_visible}
                                onChange={(e) => setPageNoteForm(prev => ({ ...prev, is_visible: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              <label htmlFor="note-visible" className="text-sm text-slate-600">
                                Visible to clients
                              </label>
                            </div>
                            
                            {/* Preview */}
                            {pageNoteForm.content && (
                              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                                <p className="text-xs text-slate-500 mb-2">Preview:</p>
                                <div className="prose prose-sm max-w-none text-slate-700">
                                  <ReactMarkdown>{pageNoteForm.content}</ReactMarkdown>
                                </div>
                              </div>
                            )}
                            
                            <div className="flex gap-2">
                              <Button
                                onClick={savePageNote}
                                disabled={savingPageNote || !pageNoteForm.page_id || !pageNoteForm.content}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                {savingPageNote ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Save Note
                              </Button>
                              {selectedPageNote && (
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedPageNote(null);
                                    setPageNoteForm({ page_id: "", title: "", content: "", is_visible: true });
                                  }}
                                >
                                  Clear
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {pageNotesTab === "market-insights" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Existing Market Insights List */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-slate-700">Existing Market Insights</h4>
                          {loadingPageNotes ? (
                            <div className="text-center py-4 text-slate-500">Loading...</div>
                          ) : marketInsights.length === 0 ? (
                            <div className="text-center py-4 text-slate-500">No market insights yet</div>
                          ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {marketInsights.map(insight => (
                                <div
                                  key={`${insight.tenant_id}-${insight.page_id}`}
                                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                    marketInsightForm.page_id === insight.page_id
                                      ? "border-blue-500 bg-blue-50"
                                      : "border-slate-200 hover:border-blue-300"
                                  }`}
                                  onClick={() => {
                                    setMarketInsightForm({
                                      page_id: insight.page_id,
                                      insight_text: insight.insight_text || "",
                                      market_context: insight.market_context || "",
                                      status: insight.status || "draft"
                                    });
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">
                                          {VALID_PAGE_IDS.find(p => p.id === insight.page_id)?.label || insight.page_id}
                                        </Badge>
                                        <Badge 
                                          variant="outline" 
                                          className={`text-xs ${
                                            insight.status === "approved" 
                                              ? "bg-green-100 text-green-700" 
                                              : "bg-amber-100 text-amber-700"
                                          }`}
                                        >
                                          {insight.status}
                                        </Badge>
                                      </div>
                                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                                        {insight.insight_text?.substring(0, 80)}...
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteMarketInsight(insight.page_id);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Generate New Insight */}
                          <div className="pt-4 border-t border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 mb-2">Generate New Insight</h5>
                            <div className="flex gap-2">
                              <select
                                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                                value={marketInsightForm.page_id || ""}
                                onChange={(e) => setMarketInsightForm(prev => ({ ...prev, page_id: e.target.value }))}
                              >
                                <option value="">-- Select page --</option>
                                {VALID_PAGE_IDS.filter(p => p.id !== "marketing_goals").map(page => (
                                  <option key={page.id} value={page.id}>{page.label}</option>
                                ))}
                              </select>
                              <Button
                                variant="outline"
                                disabled={!marketInsightForm.page_id || generatingMarketInsight}
                                onClick={() => generateMarketInsight(marketInsightForm.page_id)}
                              >
                                {generatingMarketInsight ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Sparkles className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Market Insight Editor */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-slate-700">Edit Market Insight</h4>
                          
                          {marketInsightForm.page_id ? (
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-slate-500">Page</label>
                                <Input
                                  value={VALID_PAGE_IDS.find(p => p.id === marketInsightForm.page_id)?.label || marketInsightForm.page_id}
                                  disabled
                                  className="bg-slate-50"
                                />
                              </div>
                              
                              <div>
                                <label className="text-xs text-slate-500">Market Context (e.g., "Google Core Update Q1 2026")</label>
                                <Input
                                  value={marketInsightForm.market_context}
                                  onChange={(e) => setMarketInsightForm(prev => ({ ...prev, market_context: e.target.value }))}
                                  placeholder="Market context label"
                                />
                              </div>
                              
                              <div>
                                <label className="text-xs text-slate-500">Insight Text (Markdown supported)</label>
                                <Textarea
                                  value={marketInsightForm.insight_text}
                                  onChange={(e) => setMarketInsightForm(prev => ({ ...prev, insight_text: e.target.value }))}
                                  placeholder="Enter market insight..."
                                  rows={8}
                                />
                              </div>
                              
                              <div>
                                <label className="text-xs text-slate-500">Status</label>
                                <select
                                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                                  value={marketInsightForm.status}
                                  onChange={(e) => setMarketInsightForm(prev => ({ ...prev, status: e.target.value }))}
                                >
                                  <option value="draft">Draft (not visible to clients)</option>
                                  <option value="approved">Approved (visible to clients)</option>
                                </select>
                              </div>
                              
                              {/* Preview */}
                              {marketInsightForm.insight_text && (
                                <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                                  <p className="text-xs text-blue-600 mb-2">Preview:</p>
                                  <div className="prose prose-sm max-w-none text-slate-700">
                                    <ReactMarkdown>{marketInsightForm.insight_text}</ReactMarkdown>
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex gap-2">
                                <Button
                                  onClick={saveMarketInsight}
                                  disabled={savingPageNote}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  {savingPageNote ? (
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                  )}
                                  {marketInsightForm.status === "approved" ? "Approve & Publish" : "Save Draft"}
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => setMarketInsightForm({ page_id: "", insight_text: "", market_context: "", status: "draft" })}
                                >
                                  Clear
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-slate-500">
                              <TrendingUp className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                              <p>Select or generate a market insight to edit</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEO Context Tab */}
          <TabsContent value="seo-context" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  Tenant SEO Context
                </CardTitle>
                <CardDescription>
                  Configure tenant-specific context for AI insight generation. This information helps create more accurate and relevant insights.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Tenant Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Select Tenant</label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={seoContextTenant?.tenant_id || ""}
                    onChange={(e) => {
                      const tenant = tenants.find(t => t.tenant_id === e.target.value);
                      setSeoContextTenant(tenant);
                      if (tenant) {
                        fetchSeoContext(tenant.tenant_id);
                      }
                    }}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {seoContextTenant && (
                  <>
                    {loadingSeoContext ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Industry */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">Industry</label>
                          <input
                            type="text"
                            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                            placeholder="e.g., B2B SaaS, Digital Marketing, Retail Technology"
                            value={seoContext.industry || ""}
                            onChange={(e) => setSeoContext(prev => ({ ...prev, industry: e.target.value }))}
                          />
                        </div>

                        {/* Recent Events */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium text-slate-700">Recent Events & Context</label>
                          <p className="text-xs text-slate-500">Add events that may affect SEO performance (redesigns, migrations, algorithm updates, etc.)</p>
                          
                          {/* Existing events */}
                          <div className="space-y-2">
                            {(seoContext.recent_events || []).map((event, idx) => (
                              <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded border">
                                <span className="text-xs font-medium text-purple-600 w-20">{event.date}</span>
                                <span className="text-sm flex-1">{event.event}</span>
                                <span className="text-xs text-slate-500 flex-1">{event.impact}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeEvent(idx)}
                                  className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>

                          {/* Add new event */}
                          <div className="grid grid-cols-4 gap-2">
                            <input
                              type="text"
                              placeholder="Date (e.g., 2025-10)"
                              className="border border-slate-300 rounded px-2 py-1 text-sm"
                              value={newEvent.date}
                              onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                            />
                            <input
                              type="text"
                              placeholder="Event (e.g., Site redesign)"
                              className="border border-slate-300 rounded px-2 py-1 text-sm"
                              value={newEvent.event}
                              onChange={(e) => setNewEvent(prev => ({ ...prev, event: e.target.value }))}
                            />
                            <input
                              type="text"
                              placeholder="Impact"
                              className="border border-slate-300 rounded px-2 py-1 text-sm"
                              value={newEvent.impact}
                              onChange={(e) => setNewEvent(prev => ({ ...prev, impact: e.target.value }))}
                            />
                            <Button size="sm" onClick={addEvent} className="bg-purple-600 hover:bg-purple-700">
                              <Plus className="w-4 h-4 mr-1" /> Add
                            </Button>
                          </div>
                        </div>

                        {/* Core Pages */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium text-slate-700">Core Pages to Monitor</label>
                          <p className="text-xs text-slate-500">List important pages (homepage, product pages, solutions) that should be prioritized in analysis.</p>
                          
                          {/* Existing pages */}
                          <div className="flex flex-wrap gap-2">
                            {(seoContext.core_pages || []).map((page, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                                {page}
                                <button onClick={() => removeCorePage(idx)} className="hover:text-red-600">
                                  <XCircle className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>

                          {/* Add new page */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="e.g., /solutions or https://example.com/products"
                              className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                              value={newCorePage}
                              onChange={(e) => setNewCorePage(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && addCorePage()}
                            />
                            <Button size="sm" onClick={addCorePage} className="bg-purple-600 hover:bg-purple-700">
                              <Plus className="w-4 h-4 mr-1" /> Add
                            </Button>
                          </div>
                        </div>

                        {/* Additional Notes */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">Additional Notes</label>
                          <p className="text-xs text-slate-500">Any other context the AI should consider when generating insights.</p>
                          <textarea
                            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[100px]"
                            placeholder="e.g., Content quality is the primary concern, not technical issues. Algorithm update recovery typically takes 3-4 months..."
                            value={seoContext.notes || ""}
                            onChange={(e) => setSeoContext(prev => ({ ...prev, notes: e.target.value }))}
                          />
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4 border-t">
                          <Button
                            onClick={saveSeoContext}
                            disabled={savingSeoContext}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {savingSeoContext ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Save SEO Context
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileBarChart className="w-5 h-5 text-purple-600" />
                  Report Builder
                </CardTitle>
                <CardDescription>
                  Create report templates with customizable widgets - control exactly what data appears in each report. 
                  Similar to Semrush's report editor.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Tenant Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Select Tenant</label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={reportsTenant?.tenant_id || ""}
                    onChange={(e) => {
                      const tenant = tenants.find(t => t.tenant_id === e.target.value);
                      setReportsTenant(tenant);
                      setSelectedReportConfig(null);
                      setReportPreview(null);
                      if (tenant) {
                        fetchReportConfigs(tenant.tenant_id);
                        fetchGeneratedReports(tenant.tenant_id);
                      }
                    }}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {reportsTenant && (
                  <div className="space-y-6">
                    {/* Two Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Left: Existing Templates */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Report Templates
                        </h4>
                        
                        {loadingReportConfigs ? (
                          <div className="text-center py-4 text-slate-500">
                            <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2" />
                            Loading...
                          </div>
                        ) : reportConfigs.length === 0 ? (
                          <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                            <FileBarChart className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                            <p className="text-sm">No templates yet</p>
                            <p className="text-xs text-slate-400">Create one below</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {reportConfigs.map(config => (
                              <div
                                key={config.template_id}
                                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                  selectedReportConfig?.template_id === config.template_id
                                    ? "border-purple-500 bg-purple-50 shadow-sm"
                                    : "border-slate-200 hover:border-purple-300 hover:bg-slate-50"
                                }`}
                                onClick={() => {
                                  setSelectedReportConfig(config);
                                  setReportPreview(null);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-800 truncate">{config.name}</p>
                                    <p className="text-xs text-slate-500">
                                      {(config.pages || []).filter(p => p.enabled !== false).length} pages • {config.date_range}
                                    </p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteReportConfig(config.template_id);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Create New Template */}
                        <div className="pt-4 border-t border-slate-200 space-y-3">
                          <h5 className="text-sm font-medium text-slate-700">New Template</h5>
                          
                          <Input
                            value={reportConfigForm.name}
                            onChange={(e) => setReportConfigForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Template name"
                            className="text-sm"
                          />
                          
                          <div>
                            <label className="text-xs text-slate-500">Date Range</label>
                            <select
                              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                              value={reportConfigForm.date_range}
                              onChange={(e) => setReportConfigForm(prev => ({ ...prev, date_range: e.target.value }))}
                            >
                              <option value="7d">Last 7 days</option>
                              <option value="30d">Last 30 days</option>
                              <option value="90d">Last 90 days</option>
                            </select>
                          </div>

                          {/* Report Options */}
                          <div className="space-y-2 pt-2 border-t border-slate-100">
                            <label className="text-xs text-slate-500 font-medium">Report Options</label>
                            
                            {/* Table of Contents */}
                            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={reportConfigForm.include_toc}
                                onChange={(e) => setReportConfigForm(prev => ({ ...prev, include_toc: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Include Table of Contents
                            </label>
                            
                            {/* Comparison Period */}
                            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={reportConfigForm.show_comparison}
                                onChange={(e) => setReportConfigForm(prev => ({ ...prev, show_comparison: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Show Comparison Period
                            </label>
                            
                            {reportConfigForm.show_comparison && (
                              <select
                                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                                value={reportConfigForm.comparison_period}
                                onChange={(e) => setReportConfigForm(prev => ({ ...prev, comparison_period: e.target.value }))}
                              >
                                <option value="previous">Previous Period (auto)</option>
                                <option value="yoy">Year over Year</option>
                              </select>
                            )}
                          </div>
                          
                          <Button 
                            onClick={createReportConfig} 
                            className="w-full bg-purple-600 hover:bg-purple-700"
                            disabled={!reportConfigForm.name}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Template
                          </Button>
                        </div>
                      </div>

                      {/* Middle: Widget Configuration */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Pages & Widgets
                        </h4>
                        
                        {selectedReportConfig ? (
                          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                            {Object.entries(availableWidgets).map(([pageId, pageInfo]) => {
                              const pageConfig = (selectedReportConfig.pages || []).find(p => p.page_id === pageId);
                              const pageEnabled = pageConfig?.enabled !== false;
                              
                              // Get widget order from config or use default
                              const orderedWidgets = pageConfig?.widgets?.length > 0
                                ? pageConfig.widgets
                                    .map(wc => pageInfo.widgets?.find(w => w.id === wc.widget_id))
                                    .filter(Boolean)
                                    .concat(pageInfo.widgets?.filter(w => !pageConfig.widgets.some(wc => wc.widget_id === w.id)) || [])
                                : (pageInfo.widgets || []);
                              
                              return (
                                <div key={pageId} className="border border-slate-200 rounded-lg overflow-hidden">
                                  {/* Page Header */}
                                  <div 
                                    className={`px-3 py-2 flex items-center justify-between cursor-pointer ${
                                      pageEnabled ? "bg-purple-50" : "bg-slate-50"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={pageEnabled}
                                        onChange={(e) => togglePageInConfig(pageId, e.target.checked)}
                                        className="rounded border-slate-300 text-purple-600"
                                      />
                                      <span className={`text-sm font-medium ${pageEnabled ? "text-purple-800" : "text-slate-500"}`}>
                                        {pageInfo.page_title}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-slate-400">Drag to reorder</span>
                                      <Badge variant="outline" className="text-xs">
                                        {pageInfo.widgets?.length || 0} widgets
                                      </Badge>
                                    </div>
                                  </div>
                                  
                                  {/* Widgets List with Drag & Drop */}
                                  {pageEnabled && (
                                    <div className="px-3 py-2 bg-white">
                                      <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={(event) => handleWidgetDragEnd(event, pageId, orderedWidgets)}
                                      >
                                        <SortableContext
                                          items={orderedWidgets.map(w => w.id)}
                                          strategy={verticalListSortingStrategy}
                                        >
                                          {orderedWidgets.map(widget => {
                                            const widgetConfig = (pageConfig?.widgets || []).find(w => w.widget_id === widget.id);
                                            const widgetEnabled = widgetConfig?.enabled !== false;
                                            
                                            return (
                                              <SortableWidgetItem
                                                key={widget.id}
                                                widget={widget}
                                                pageId={pageId}
                                                widgetEnabled={widgetEnabled}
                                                onToggle={toggleWidgetInConfig}
                                              />
                                            );
                                          })}
                                        </SortableContext>
                                      </DndContext>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-12 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                            <Settings className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                            <p className="text-sm">Select a template</p>
                            <p className="text-xs text-slate-400">to configure widgets</p>
                          </div>
                        )}
                      </div>

                      {/* Right: Preview & Generate */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Preview & Generate
                        </h4>
                        
                        {selectedReportConfig ? (
                          <div className="space-y-4">
                            {/* Template Info */}
                            <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/50">
                              <h5 className="font-medium text-purple-800">{selectedReportConfig.name}</h5>
                              <p className="text-xs text-purple-600 mt-1">
                                {selectedReportConfig.tenant_name} • {selectedReportConfig.date_range}
                              </p>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="space-y-2">
                              <Button
                                variant="outline"
                                onClick={() => previewReport(selectedReportConfig.template_id)}
                                disabled={loadingReportPreview}
                                className="w-full"
                              >
                                {loadingReportPreview ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Eye className="w-4 h-4 mr-2" />
                                )}
                                Load Preview
                              </Button>
                              
                              <Button
                                onClick={() => generateReport("html")}
                                disabled={generatingReport || !reportPreview}
                                className="w-full bg-green-600 hover:bg-green-700"
                              >
                                {generatingReport ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4 mr-2" />
                                )}
                                Generate HTML Report
                              </Button>

                              {/* Export Options */}
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={exportChartsAsZip}
                                  disabled={exportingCharts}
                                  className="flex-1"
                                >
                                  {exportingCharts ? (
                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <ImageIcon className="w-4 h-4 mr-1" />
                                  )}
                                  Charts as PNG
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={exportFullReport}
                                  disabled={exportingFullReport}
                                  className="flex-1"
                                >
                                  {exportingFullReport ? (
                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <FileArchive className="w-4 h-4 mr-1" />
                                  )}
                                  Full ZIP
                                </Button>
                              </div>
                            </div>
                            
                            {/* Data Sources Status */}
                            {reportPreview?.data_sources && (
                              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                                <h6 className="text-xs font-medium text-slate-700 mb-2">Data Sources</h6>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                  {Object.entries(reportPreview.data_sources).map(([source, status]) => (
                                    <div key={source} className="flex items-center gap-1">
                                      <span className={`w-2 h-2 rounded-full ${
                                        status === true || status !== "none" ? "bg-green-500" : "bg-slate-300"
                                      }`}></span>
                                      <span className="text-slate-600 capitalize">{source.replace("_", " ")}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Editable Sections */}
                            {reportPreview && (
                              <div className="space-y-4">
                                <h6 className="text-xs font-medium text-slate-700">
                                  Edit Report Sections
                                </h6>
                                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                                  {reportPreview.sections
                                    .filter(s => s.editable)
                                    .map(section => (
                                      <div key={section.section_id} className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">
                                          {section.title || section.section_id}
                                        </label>
                                        <RichTextEditor
                                          content={editingSections[section.section_id] || section.content || ""}
                                          onChange={(html) => setEditingSections(prev => ({
                                            ...prev,
                                            [section.section_id]: html
                                          }))}
                                          placeholder={`Edit ${section.title || 'content'}...`}
                                          minHeight="120px"
                                        />
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Schedule Section */}
                            <div className="pt-4 border-t border-slate-200">
                              <h6 className="text-xs font-medium text-slate-700 mb-3 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Scheduled Reports
                              </h6>
                              
                              {/* Enable/Disable Toggle */}
                              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={scheduleConfig.enabled}
                                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                                  className="rounded border-slate-300 text-purple-600"
                                />
                                <span className="text-sm text-slate-600">Enable scheduled delivery</span>
                              </label>
                              
                              {scheduleConfig.enabled && (
                                <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                                  {/* Frequency */}
                                  <div>
                                    <label className="text-xs text-slate-500">Frequency</label>
                                    <select
                                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm mt-1"
                                      value={scheduleConfig.frequency}
                                      onChange={(e) => setScheduleConfig(prev => ({ ...prev, frequency: e.target.value }))}
                                    >
                                      <option value="daily">Daily</option>
                                      <option value="weekly">Weekly</option>
                                      <option value="monthly">Monthly</option>
                                    </select>
                                  </div>
                                  
                                  {/* Day selection */}
                                  {scheduleConfig.frequency === "weekly" && (
                                    <div>
                                      <label className="text-xs text-slate-500">Day of Week</label>
                                      <select
                                        className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm mt-1"
                                        value={scheduleConfig.day_of_week}
                                        onChange={(e) => setScheduleConfig(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                                      >
                                        <option value={0}>Monday</option>
                                        <option value={1}>Tuesday</option>
                                        <option value={2}>Wednesday</option>
                                        <option value={3}>Thursday</option>
                                        <option value={4}>Friday</option>
                                        <option value={5}>Saturday</option>
                                        <option value={6}>Sunday</option>
                                      </select>
                                    </div>
                                  )}
                                  
                                  {scheduleConfig.frequency === "monthly" && (
                                    <div>
                                      <label className="text-xs text-slate-500">Day of Month</label>
                                      <select
                                        className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm mt-1"
                                        value={scheduleConfig.day_of_month}
                                        onChange={(e) => setScheduleConfig(prev => ({ ...prev, day_of_month: parseInt(e.target.value) }))}
                                      >
                                        {[...Array(28)].map((_, i) => (
                                          <option key={i+1} value={i+1}>{i+1}</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                  
                                  {/* Time */}
                                  <div>
                                    <label className="text-xs text-slate-500">Time (UTC)</label>
                                    <input
                                      type="time"
                                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm mt-1"
                                      value={scheduleConfig.time_utc}
                                      onChange={(e) => setScheduleConfig(prev => ({ ...prev, time_utc: e.target.value }))}
                                    />
                                  </div>
                                  
                                  {/* Recipients */}
                                  <div>
                                    <label className="text-xs text-slate-500">Recipients</label>
                                    <div className="flex gap-2 mt-1">
                                      <input
                                        type="email"
                                        className="flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                        placeholder="email@example.com"
                                        value={newRecipient}
                                        onChange={(e) => setNewRecipient(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                                      />
                                      <Button size="sm" variant="outline" onClick={addRecipient}>
                                        <Plus className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    
                                    {scheduleConfig.recipients.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {scheduleConfig.recipients.map(email => (
                                          <span 
                                            key={email}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs"
                                          >
                                            {email}
                                            <button 
                                              onClick={() => removeRecipient(email)}
                                              className="hover:text-purple-900"
                                            >
                                              <XCircle className="w-3 h-3" />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Include ZIP */}
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={scheduleConfig.include_zip}
                                      onChange={(e) => setScheduleConfig(prev => ({ ...prev, include_zip: e.target.checked }))}
                                      className="rounded border-slate-300 text-purple-600"
                                    />
                                    <span className="text-xs text-slate-600">Include chart images (ZIP attachment)</span>
                                  </label>
                                  
                                  {/* Actions */}
                                  <div className="flex gap-2 pt-2">
                                    <Button
                                      size="sm"
                                      onClick={saveSchedule}
                                      disabled={savingSchedule || scheduleConfig.recipients.length === 0}
                                      className="flex-1"
                                    >
                                      {savingSchedule ? (
                                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                      ) : (
                                        <Clock className="w-3 h-3 mr-1" />
                                      )}
                                      Save Schedule
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={sendTestEmail}
                                      disabled={sendingTestEmail || !newRecipient}
                                    >
                                      {sendingTestEmail ? (
                                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                      ) : (
                                        <Send className="w-3 h-3 mr-1" />
                                      )}
                                      Test
                                    </Button>
                                  </div>
                                  
                                  {/* Next Run Info */}
                                  {selectedReportConfig?.schedule?.next_run && (
                                    <p className="text-xs text-slate-500 mt-2">
                                      Next run: {new Date(selectedReportConfig.schedule.next_run).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-12 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                            <FileBarChart className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                            <p className="text-sm">Select a template</p>
                            <p className="text-xs text-slate-400">to preview and generate</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Generated Reports History */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Generated Reports History
                        </h5>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => fetchGeneratedReports(reportsTenant?.tenant_id)}
                          disabled={loadingGeneratedReports}
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${loadingGeneratedReports ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                      
                      {loadingGeneratedReports ? (
                        <div className="text-center py-8 text-slate-500">
                          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                          <p className="text-sm">Loading reports...</p>
                        </div>
                      ) : generatedReports.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">No reports generated yet</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {generatedReports.map(report => (
                            <div 
                              key={report.report_id}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-purple-600" />
                                <div>
                                  <p className="text-sm font-medium text-slate-800">{report.template_name || "Report"}</p>
                                  <p className="text-xs text-slate-500">
                                    {new Date(report.generated_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    try {
                                      const response = await axios.get(`${API}/reports/download/${report.report_id}`, {
                                        params: { Authorization: `Bearer ${sessionToken}` }
                                      });
                                      const blob = new Blob([response.data], { type: 'text/html' });
                                      const url = URL.createObjectURL(blob);
                                      window.open(url, '_blank');
                                    } catch (err) {
                                      toast.error("Failed to open report");
                                    }
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => deleteGeneratedReport(report.report_id)}
                                  disabled={deletingReport === report.report_id}
                                >
                                  {deletingReport === report.report_id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Flags Tab */}
          <TabsContent value="feature-flags" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: All Feature Flags */}
              <div className="lg:col-span-2 space-y-4">
                <Card className="bg-white border-slate-200">
                  <CardHeader className="border-b border-slate-200 flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-slate-900 flex items-center gap-2">
                        <Flag className="w-5 h-5 text-purple-600" />
                        Feature Flags
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Control feature visibility across all tenants
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { fetchFeatureFlags(); fetchFeatureFlagsMetadata(); }}
                        disabled={loadingFeatureFlags}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingFeatureFlags ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowNewFlagDialog(true)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Flag
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {loadingFeatureFlags ? (
                      <div className="flex items-center justify-center py-12">
                        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : featureFlags.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Flag className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No feature flags yet</p>
                        <p className="text-sm mt-1">Create your first feature flag to control feature visibility</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-200">
                        {featureFlags.map((flag) => (
                          <div key={flag.feature_id} className="p-4 hover:bg-slate-50">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <span className="text-2xl">{getCategoryIcon(flag.category)}</span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-slate-900">{flag.name}</h4>
                                    <Badge variant="outline" className={`text-xs ${getStatusColor(flag.status)}`}>
                                      {flag.status}
                                    </Badge>
                                    {flag.override_count > 0 && (
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                        {flag.override_count} override{flag.override_count > 1 ? 's' : ''}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1">{flag.description}</p>
                                  <p className="text-xs text-slate-400 mt-1 font-mono">{flag.feature_id}</p>
                                  {flag.release_notes && (
                                    <p className="text-xs text-slate-400 mt-2 italic">{flag.release_notes}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Default Toggle */}
                                <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-100">
                                  <span className="text-xs text-slate-600">Default:</span>
                                  <button
                                    onClick={() => updateFeatureFlag(flag.feature_id, { default_enabled: !flag.default_enabled })}
                                    className={`relative w-10 h-5 rounded-full transition-colors ${
                                      flag.default_enabled ? 'bg-green-500' : 'bg-slate-300'
                                    }`}
                                  >
                                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                      flag.default_enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`} />
                                  </button>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingFlag(flag)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteFeatureFlag(flag.feature_id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Tenant Overrides */}
              <div className="space-y-4">
                <Card className="bg-white border-slate-200">
                  <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Beaker className="w-5 h-5 text-amber-600" />
                      Tenant Overrides
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Beta test features with specific tenants
                    </p>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {/* Tenant Selector */}
                    <div className="mb-4">
                      <Label className="text-slate-700 mb-2 block">Select Tenant</Label>
                      <Select
                        value={selectedFlagTenant?.tenant_id || ""}
                        onValueChange={(value) => {
                          const tenant = tenants.find(t => t.tenant_id === value);
                          setSelectedFlagTenant(tenant);
                          if (tenant) fetchTenantOverrides(tenant.tenant_id);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a tenant..." />
                        </SelectTrigger>
                        <SelectContent>
                          {tenants.map((tenant) => (
                            <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                              {tenant.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedFlagTenant ? (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-slate-700 pb-2 border-b">
                          Overrides for {selectedFlagTenant.name}
                        </p>
                        {featureFlags.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-4">No feature flags to configure</p>
                        ) : (
                          featureFlags.map((flag) => {
                            const hasOverride = flag.feature_id in tenantFlagOverrides;
                            const overrideValue = tenantFlagOverrides[flag.feature_id];
                            const effectiveValue = hasOverride ? overrideValue : flag.default_enabled;
                            
                            return (
                              <div key={flag.feature_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-700">{flag.name}</span>
                                    {hasOverride && (
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                        Override
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    Default: {flag.default_enabled ? "On" : "Off"}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Three-way toggle: Default / On / Off */}
                                  <div className="flex rounded-md overflow-hidden border border-slate-200">
                                    <button
                                      onClick={() => setTenantOverride(flag.feature_id, null)}
                                      className={`px-2 py-1 text-xs ${!hasOverride ? 'bg-slate-200 text-slate-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                                      title="Use default"
                                    >
                                      Default
                                    </button>
                                    <button
                                      onClick={() => setTenantOverride(flag.feature_id, true)}
                                      className={`px-2 py-1 text-xs border-l border-slate-200 ${hasOverride && overrideValue === true ? 'bg-green-100 text-green-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                                      title="Enable for this tenant"
                                    >
                                      On
                                    </button>
                                    <button
                                      onClick={() => setTenantOverride(flag.feature_id, false)}
                                      className={`px-2 py-1 text-xs border-l border-slate-200 ${hasOverride && overrideValue === false ? 'bg-red-100 text-red-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                                      title="Disable for this tenant"
                                    >
                                      Off
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                        <p className="text-sm">Select a tenant to manage their feature overrides</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Status Legend */}
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-700">Status Legend</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">development</Badge>
                      <span className="text-xs text-slate-500">Not ready for any users</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">beta</Badge>
                      <span className="text-xs text-slate-500">Testing with select tenants</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">stable</Badge>
                      <span className="text-xs text-slate-500">Ready for all users</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">deprecated</Badge>
                      <span className="text-xs text-slate-500">Being phased out</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* New Flag Dialog */}
            <Dialog open={showNewFlagDialog} onOpenChange={setShowNewFlagDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Feature Flag</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Feature ID</Label>
                      <Input
                        placeholder="deal_probability"
                        value={newFlagForm.feature_id}
                        onChange={(e) => setNewFlagForm(prev => ({ ...prev, feature_id: e.target.value }))}
                      />
                      <p className="text-xs text-slate-500">Used in code (snake_case)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Display Name</Label>
                      <Input
                        placeholder="Deal Close Probability"
                        value={newFlagForm.name}
                        onChange={(e) => setNewFlagForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="What does this feature do?"
                      value={newFlagForm.description}
                      onChange={(e) => setNewFlagForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={newFlagForm.category}
                        onValueChange={(value) => setNewFlagForm(prev => ({ ...prev, category: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="deals">💰 Deals & Pipeline</SelectItem>
                          <SelectItem value="reports">📊 Reports & Export</SelectItem>
                          <SelectItem value="ai">🤖 AI Features</SelectItem>
                          <SelectItem value="analytics">📈 Analytics</SelectItem>
                          <SelectItem value="social">📱 Social Media</SelectItem>
                          <SelectItem value="seo">🔍 SEO & Keywords</SelectItem>
                          <SelectItem value="goals">🎯 Goals & Tracking</SelectItem>
                          <SelectItem value="traffic">🌐 Website Traffic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={newFlagForm.status}
                        onValueChange={(value) => setNewFlagForm(prev => ({ ...prev, status: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="development">Development</SelectItem>
                          <SelectItem value="beta">Beta</SelectItem>
                          <SelectItem value="stable">Stable</SelectItem>
                          <SelectItem value="deprecated">Deprecated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Release Notes</Label>
                    <Textarea
                      placeholder="v1.0 - Initial release..."
                      value={newFlagForm.release_notes}
                      onChange={(e) => setNewFlagForm(prev => ({ ...prev, release_notes: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="defaultEnabled"
                      checked={newFlagForm.default_enabled}
                      onChange={(e) => setNewFlagForm(prev => ({ ...prev, default_enabled: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <div>
                      <Label htmlFor="defaultEnabled" className="cursor-pointer">Enable by default</Label>
                      <p className="text-xs text-slate-500">If checked, all tenants will see this feature immediately</p>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewFlagDialog(false)}>Cancel</Button>
                  <Button onClick={createFeatureFlag} disabled={savingFlag} className="bg-purple-600 hover:bg-purple-700">
                    {savingFlag ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Flag
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Flag Dialog */}
            <Dialog open={!!editingFlag} onOpenChange={(open) => !open && setEditingFlag(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Feature Flag</DialogTitle>
                </DialogHeader>
                {editingFlag && (
                  <div className="space-y-4 py-4">
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <p className="text-sm font-mono text-slate-600">{editingFlag.feature_id}</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Display Name</Label>
                      <Input
                        value={editingFlag.name}
                        onChange={(e) => setEditingFlag(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={editingFlag.description || ""}
                        onChange={(e) => setEditingFlag(prev => ({ ...prev, description: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={editingFlag.category}
                          onValueChange={(value) => setEditingFlag(prev => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="deals">💰 Deals & Pipeline</SelectItem>
                            <SelectItem value="reports">📊 Reports & Export</SelectItem>
                            <SelectItem value="ai">🤖 AI Features</SelectItem>
                            <SelectItem value="analytics">📈 Analytics</SelectItem>
                            <SelectItem value="social">📱 Social Media</SelectItem>
                            <SelectItem value="seo">🔍 SEO & Keywords</SelectItem>
                            <SelectItem value="goals">🎯 Goals & Tracking</SelectItem>
                            <SelectItem value="traffic">🌐 Website Traffic</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={editingFlag.status}
                          onValueChange={(value) => setEditingFlag(prev => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="development">Development</SelectItem>
                            <SelectItem value="beta">Beta</SelectItem>
                            <SelectItem value="stable">Stable</SelectItem>
                            <SelectItem value="deprecated">Deprecated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Release Notes</Label>
                      <Textarea
                        value={editingFlag.release_notes || ""}
                        onChange={(e) => setEditingFlag(prev => ({ ...prev, release_notes: e.target.value }))}
                        rows={2}
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingFlag(null)}>Cancel</Button>
                  <Button 
                    onClick={() => updateFeatureFlag(editingFlag.feature_id, {
                      name: editingFlag.name,
                      description: editingFlag.description,
                      category: editingFlag.category,
                      status: editingFlag.status,
                      release_notes: editingFlag.release_notes
                    })}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>

      {/* Set Password Dialog */}
      <Dialog open={setPasswordDialog.open} onOpenChange={(open) => !open && setSetPasswordDialog({ open: false, user: null, tenantId: null })}>
        <DialogContent className="bg-slate-50 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Set Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-500">
              Setting password for: <span className="text-slate-900 font-medium">{setPasswordDialog.user?.email}</span>
            </p>
            <div className="space-y-2">
              <Label className="text-slate-600">New Password</Label>
              <Input
                type="password"
                placeholder="Enter new password (min 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-slate-300 text-slate-600"
              onClick={() => setSetPasswordDialog({ open: false, user: null, tenantId: null })}
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSetPassword}
              disabled={settingPassword || newPassword.length < 8}
            >
              {settingPassword ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={announcementDialog.open} onOpenChange={(open) => setAnnouncementDialog({ open, editing: announcementDialog.editing })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {announcementDialog.editing ? "Edit Announcement" : "Create Announcement"}
            </DialogTitle>
            <DialogDescription>
              Announcements are visible to all users on the platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ann-title">Title</Label>
              <Input
                id="ann-title"
                placeholder="Announcement title"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ann-content">Content</Label>
              <Textarea
                id="ann-content"
                placeholder="Announcement content (HTML supported)"
                value={newAnnouncement.content}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
              />
              <p className="text-xs text-slate-500">Basic HTML tags are supported for formatting</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ann-type">Type</Label>
                <Select
                  value={newAnnouncement.type}
                  onValueChange={(value) => setNewAnnouncement(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ann-expires">Expires (optional)</Label>
                <Input
                  id="ann-expires"
                  type="datetime-local"
                  value={newAnnouncement.expires_at ? newAnnouncement.expires_at.slice(0, 16) : ""}
                  onChange={(e) => setNewAnnouncement(prev => ({ 
                    ...prev, 
                    expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" 
                  }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnouncementDialog({ open: false, editing: null })}>
              Cancel
            </Button>
            <Button onClick={saveAnnouncement} disabled={savingAnnouncement}>
              {savingAnnouncement && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {announcementDialog.editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
